import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropertyMessageCard from '../property/PropertyMessageCard';
import AddressAutocomplete from '../common/AddressAutocomplete';
import NotesPanel from '../notes/NotesPanel';
import { getViewerTimezone } from '../../utils/dateTime';
import resendIcon from '../../assets/resend-icon.png';
import './TransactionChatParties.css';
import stateCityData from '../../data/state_city.json';
import { SERVICE_TYPE_OPTIONS } from '../../data/serviceTypes';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { fetchLinkPreview, parseStructuredAddress, resolveShortUrl } from '../../utils/linkPreview';
import { formatUSPhoneInput, toE164US } from '../../utils/phone';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, getDoc, doc, query, where, limit, onSnapshot, orderBy } from 'firebase/firestore';

// Narrow, deliberate match — mirrors the same check in InviteSomeonePage.js's handleSendInvite.
// Anything else (validation, permission, network errors) must NOT show a "Resend" action,
// since resending wouldn't fix those failures.
const isPendingInvitationError = (msg) =>
  (msg || '').toLowerCase().includes('pending invitation already exists');

// Humanize a backend field name for display: `phone` → "Phone",
// `firstName` → "First name", `licenseNumber` → "License number".
const humanizeField = (field) => {
  if (!field) return '';
  const spaced = field.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Extract a user-friendly message from a backend Bean-Validation response.
// Backend returns `{message: "Validation failed", details: [{field, message}]}`
// — rolling the field names into the surfaced string avoids showing just
// "Validation failed" with no hint of what to fix.
const formatBackendErrorMessage = (responseData, fallback) => {
  const details = responseData && responseData.details;
  if (Array.isArray(details) && details.length > 0) {
    const parts = details
      .map((d) => {
        const field = humanizeField(d && d.field);
        const msg = (d && d.message) || '';
        if (!msg) return '';
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join('; ');
  }
  return (responseData && responseData.message) || fallback || '';
};

// Display labels for participant roles — mirrors rialty-mobile-app's ROLE_DISPLAY_NAMES
// (utils/transactionFormatters.ts), except TC is spelled out in full on web.
// Display-only: participant.role keeps the raw backend value, which search and
// getClientTypeFromRole depend on.
const ROLE_DISPLAY_NAMES = {
  LISTING_AGENT: 'Listing Agent',
  BUYERS_AGENT: "Buyer's Agent",
  BROKER: 'Broker',
  BUYER: 'Buyer',
  SELLER: 'Seller',
  CO_LISTING_AGENT: 'Co-Listing Agent',
  CO_BUYERS_AGENT: "Co-Buyer's Agent",
  TRANSACTION_COORDINATOR: 'Transaction Coordinator',
  ESCROW_OFFICER: 'Escrow',
  TITLE_OFFICER: 'Title',
  LENDER: 'Lender',
  LOAN_OFFICER: 'Loan Officer',
  APPRAISER: 'Appraiser',
  INSPECTOR: 'Inspector',
  ATTORNEY: 'Attorney',
  INSURANCE_AGENT: 'Insurance',
  HOME_WARRANTY: 'Warranty',
  PHOTOGRAPHER: 'Photographer',
  STAGER: 'Stager',
  SERVICE_PROVIDER: 'Service Provider',
  OTHER: 'Other',
};

// "BUYERS_AGENT" / "buyers agent" → "Buyer's Agent"; unmapped roles → Title Case.
const formatRoleLabel = (role) => {
  const key = (role || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!key) return 'Participant';
  return ROLE_DISPLAY_NAMES[key] ||
    key.split('_').filter(Boolean).map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
};

const TransactionChatParties = ({ transaction, participants: initialParticipants = [], accessControl, onParticipantsChange }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [showInvitePartyModal, setShowInvitePartyModal] = useState(false);
  const [showRefPartyModal, setShowRefPartyModal] = useState(false);
  const [refPartyForm, setRefPartyForm] = useState({ email: '', firstName: '', middleName: '', lastName: '', phone: '', role: '' });
  const [isAddingRefParty, setIsAddingRefParty] = useState(false);
  const [refPartyError, setRefPartyError] = useState('');
  // Seeded with the offline fallback; replaced by GET /api/v1/service-types
  // the first time the Ref Party modal opens (same source as InviteSomeonePage).
  const [refPartyServiceTypes, setRefPartyServiceTypes] = useState(SERVICE_TYPE_OPTIONS);
  const refPartyServiceTypesFetchedRef = useRef(false);

  // Transaction Inbox
  const [copiedInboxEmail, setCopiedInboxEmail] = useState(false);

  // Platform-invite flow (invite a reference party to join Rialty — no
  // transaction access granted on acceptance). Loading state is keyed by
  // participant id so the correct row's spinner fires. Prompt modal opens
  // when the backend returns EMAIL_REQUIRED.
  const [platformInvitingId, setPlatformInvitingId] = useState(null);
  const [platformInviteError, setPlatformInviteError] = useState('');
  const [showPlatformEmailPrompt, setShowPlatformEmailPrompt] = useState(false);
  const [platformPromptParticipant, setPlatformPromptParticipant] = useState(null);
  const [platformPromptEmail, setPlatformPromptEmail] = useState('');
  const [platformPromptPhone, setPlatformPromptPhone] = useState('');
  const [platformPromptSubmitting, setPlatformPromptSubmitting] = useState(false);
  const [platformPromptError, setPlatformPromptError] = useState('');

  // Email-check state for the "Ref Party with email check" flow. refExistingUser
  // is set only after a successful check that found a registered user — the
  // "Add Reference" branch keys off this. refEmailChecked distinguishes
  // "haven't checked yet" from "checked and found nothing" so the UI shows
  // the right affordance (Check button vs Add & Invite).
  const [refEmailChecking, setRefEmailChecking] = useState(false);
  const [refEmailChecked, setRefEmailChecked] = useState(false);
  const [refExistingUser, setRefExistingUser] = useState(null);
  const [refEmailStatusMessage, setRefEmailStatusMessage] = useState('');

  const handleCopyInboxEmail = () => {
    const email = transaction?.transactionEmail;
    if (!email) return;
    navigator.clipboard.writeText(email).then(() => {
      setCopiedInboxEmail(true);
      setTimeout(() => setCopiedInboxEmail(false), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = email;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedInboxEmail(true);
      setTimeout(() => setCopiedInboxEmail(false), 2000);
    });
  };

  const handleShareInboxEmail = async () => {
    const email = transaction?.transactionEmail;
    if (!email) return;
    const shareText = `Transaction Inbox: ${email}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Transaction Inbox', text: shareText });
      } catch (_) { /* user cancelled */ }
    } else {
      window.open(`mailto:?subject=Transaction%20Inbox&body=${encodeURIComponent(shareText)}`);
    }
  };
  const [selectedPartyType, setSelectedPartyType] = useState(null);
  const [inviteStep, setInviteStep] = useState(1); // 1 = select role, 2 = enter details

  // Email check states
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailCheckResult, setEmailCheckResult] = useState(null); // { exists: boolean, user: object | null }
  const [emailCheckError, setEmailCheckError] = useState('');

  // Invitation result states
  const [invitedPersonInfo, setInvitedPersonInfo] = useState(null); // { name, email }
  const [invitationError, setInvitationError] = useState(null);

  // Participants state
  const [participantsData, setParticipantsData] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(true);
  const [participantsError, setParticipantsError] = useState(null);

  // Chat messages state
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [sendMessageError, setSendMessageError] = useState('');
  const [isGroupChatSelected, setIsGroupChatSelected] = useState(false);
  // Resolved transaction group chat ID (via Firestore dealId query or API creation)
  const [resolvedTransactionChatId, setResolvedTransactionChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesListenerRef = useRef(null);   // Firestore real-time messages unsubscribe
  const decryptedCacheRef = useRef(new Map()); // messageId → decrypted content cache
  // Link preview state
  const [linkPreview, setLinkPreview] = useState(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const linkPreviewTimeoutRef = useRef(null);
  const lastDetectedUrlRef = useRef(null);
  const linkPreviewDismissedRef = useRef(false);
  // Attachment state
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  // Stores normalized ATTOM property data after URL paste (used for interestedProperties call on send)
  const propertyPreviewRef = useRef(null);
  // Stores the canonical source URL (resolves short URLs like redf.in → redfin.com) for sourceUrl on send
  const resolvedSourceUrlRef = useRef(null);

  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    firstName: '',
    middleName: '',
    lastName: '',
    countryCode: '+1',
    phone: '',
    company: '',
    // Buyer specific fields
    preferredAddress: '',
    preferredState: '',
    preferredCity: '',
    preferredZipCode: '',
    // Seller specific fields
    address: '',
    state: '',
    city: '',
    zipCode: '',
    // Common
    notes: ''
  });

  // Country codes list with US as default
  const countryCodes = [
    { code: '+1', country: 'US', flag: '🇺🇸' },
    { code: '+1', country: 'CA', flag: '🇨🇦' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+91', country: 'IN', flag: '🇮🇳' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+52', country: 'MX', flag: '🇲🇽' },
    { code: '+55', country: 'BR', flag: '🇧🇷' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+82', country: 'KR', flag: '🇰🇷' },
    { code: '+31', country: 'NL', flag: '🇳🇱' },
    { code: '+46', country: 'SE', flag: '🇸🇪' },
    { code: '+41', country: 'CH', flag: '🇨🇭' },
    { code: '+65', country: 'SG', flag: '🇸🇬' },
    { code: '+971', country: 'AE', flag: '🇦🇪' },
    { code: '+966', country: 'SA', flag: '🇸🇦' },
  ];

  // Party types for invite modal
  const allPartyTypes = [
    { id: 'listing_agent', name: 'Listing Agent', description: "Seller's representative", icon: '👤', color: '#5B47D6' },
    { id: 'buyers_agent', name: "Buyer's Agent", description: "Buyer's representative", icon: '👤', color: '#5B47D6' },
    { id: 'co_listing_agent', name: 'Co-Listing Agent', description: 'Co-listing representative', icon: '👤', color: '#5B47D6' },
    { id: 'co_buyers_agent', name: "Co-Buyer's Agent", description: "Co-buying representative", icon: '👤', color: '#5B47D6' },
    { id: 'transaction_coordinator', name: 'Transaction Coordinator', description: 'Manages paperwork', icon: '👤', color: '#2196F3' },
    { id: 'broker', name: 'Broker', description: 'Supervising broker', icon: '🏢', color: '#8E8E93' },
    { id: 'seller', name: 'Seller', description: 'Property owner', icon: '🏠', color: '#FF9500' },
    { id: 'buyer', name: 'Buyer', description: 'Property purchaser', icon: '🏠', color: '#FF9500' },
    { id: 'escrow_officer', name: 'Escrow Officer', description: 'Handles escrow', icon: '🏦', color: '#4ECDC4' },
    { id: 'title_officer', name: 'Title Officer', description: 'Title & insurance', icon: '📋', color: '#FF9500' },
    { id: 'lender', name: 'Lender', description: 'Financing provider', icon: '💰', color: '#F59E0B' },
    { id: 'home_inspector', name: 'Home Inspector', description: 'Property inspection', icon: '🔍', color: '#E91E63' },
    { id: 'appraiser', name: 'Appraiser', description: 'Property valuation', icon: '📊', color: '#4CAF50' },
    { id: 'attorney', name: 'Attorney', description: 'Legal counsel', icon: '⚖️', color: '#9C27B0' }
  ];

  // Reference-party roles for the other side of the deal: a buying transaction
  // adds the seller's side, a listing transaction adds the buyer's side.
  const REF_PARTY_BUYING_SIDE_ROLES = [
    { value: 'SELLER', label: 'Seller' },
    { value: 'LISTING_AGENT', label: 'Listing Agent' },
    { value: 'CO_LISTING_AGENT', label: 'Co-Listing Agent' },
  ];
  const REF_PARTY_LISTING_SIDE_ROLES = [
    { value: 'BUYER', label: 'Buyer' },
    { value: 'BUYERS_AGENT', label: "Buyer's Agent" },
    { value: 'CO_BUYERS_AGENT', label: "Co-Buyer's Agent" },
  ];
  // Service-type options are prefixed so they can't collide with a role value.
  // They are saved as role SERVICE_PROVIDER with the service type in metadata,
  // since the backend's ParticipantRole enum has no per-service-type values.
  const REF_PARTY_SERVICE_TYPE_PREFIX = 'serviceType:';

  // Get logged-in user info
  const getUserInfo = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return null;
  };

  // Resolve a state code (e.g. "CA") or full name ("California") to full name
  const resolveToFullStateName = (raw) => {
    if (!raw) return '';
    return stateCityData.find(s => s.stateCode === raw || s.state === raw)?.state || raw;
  };

  // Get agent/TC's own state, city, zip for pre-filling invite form
  const getAgentDefaultLocation = () => {
    const user = getUserInfo();
    if (!user) return {};

    const rawState = user.state || user.stateCode || user.licenseState || '';

    return {
      preferredState: resolveToFullStateName(rawState),
    };
  };

  // Check if logged-in user is an agent or transaction coordinator
  const isUserAgent = () => {
    const user = getUserInfo();
    const roles = user?.roles || user?.role || user?.userType || '';
    if (Array.isArray(roles)) {
      return roles.some(r => r.toUpperCase() === 'AGENT' || r.toUpperCase() === 'TRANSACTION_COORDINATOR');
    }
    return roles?.toUpperCase() === 'AGENT' || roles?.toUpperCase() === 'TRANSACTION_COORDINATOR';
  };

  // A row is a "reference party" when it was added via the Ref Party flow —
  // either legacy pure-contact (no identity at all) OR the new email-check
  // flow which stamps platformInviteStatus. The invite-to-platform follow-up
  // and the existing-user short-circuit are the only paths that set that
  // status, so it's a reliable discriminator for ref rows today.
  const isReferenceParty = (p) =>
    (!p.firebaseUid && !p.userId && !p.email && !p.invitedAt)
    || p.platformInviteStatus === 'ALREADY_MEMBER'
    || p.platformInviteStatus === 'INVITED'
    || p.platformInviteStatus === 'JOINED';

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check email via API
  const checkEmail = async (email) => {
    if (!email || !isValidEmail(email)) {
      setEmailCheckResult(null);
      setEmailCheckError('');
      return;
    }

    setIsCheckingEmail(true);
    setEmailCheckError('');
    setEmailCheckResult(null);

    try {
      // Get Firebase token for authorization
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setEmailCheckError('User not authenticated');
        setIsCheckingEmail(false);
        return;
      }

      const firebaseToken = await currentUser.getIdToken(true);

      const response = await apiClient.post(
        API_ENDPOINTS.checkEmail,
        { email },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Email check response:', response.data);

      // Handle API response - support multiple response structures
      if (response.data) {
        const resData = response.data;

        // Extract user object from various response formats:
        // Format 1: { exists: true, user: { ... } }
        // Format 2: { data: { ... } }
        // Format 3: { content: { ... } }
        // Format 4: Direct user object { firstName, lastName, email, ... }
        // Format 5: { exists: true, firstName, lastName, ... } (user fields at root)
        let user = null;
        let userExists = false;

        if (resData.user && typeof resData.user === 'object') {
          // Format 1: user nested in .user
          user = resData.user;
          userExists = resData.exists !== false;
        } else if (resData.data && typeof resData.data === 'object' && !Array.isArray(resData.data)) {
          // Format 2: user nested in .data
          user = resData.data;
          userExists = resData.exists !== false;
        } else if (resData.content && typeof resData.content === 'object' && !Array.isArray(resData.content)) {
          // Format 3: user nested in .content
          user = resData.content;
          userExists = resData.exists !== false;
        } else if (resData.firstName || resData.lastName || resData.email || resData.id) {
          // Format 4 & 5: user fields directly at root level
          user = resData;
          userExists = resData.exists !== false;
        }

        // Build normalized result for state
        const normalizedResult = {
          exists: userExists && user !== null,
          user: user
        };
        setEmailCheckResult(normalizedResult);

        console.log('Parsed user data from check-email:', user, 'exists:', userExists);

        // If user exists, auto-fill all their details into the form
        if (normalizedResult.exists && user) {
          const resolvedState = resolveToFullStateName(user.state || user.stateCode || user.preferredState || user.preferred_state || '');
          setInviteFormData(prev => ({
            ...prev,
            firstName: user.firstName || user.first_name || prev.firstName,
            middleName: user.middleName || user.middle_name || prev.middleName,
            lastName: user.lastName || user.last_name || prev.lastName,
            phone: user.phone || user.phoneNumber || user.phone_number || user.mobile || prev.phone,
            company: user.company || user.organization || user.companyName || user.company_name || prev.company,
            // Seller address fields
            address: user.address || user.propertyAddress || user.street || prev.address,
            state: resolvedState || prev.state,
            city: user.city || user.propertyCity || prev.city,
            zipCode: user.zipCode || user.zip || user.propertyZip || user.postalCode || user.postal_code || prev.zipCode,
            // Buyer preferred location fields
            preferredAddress: user.address || user.street || prev.preferredAddress,
            preferredState: resolvedState || prev.preferredState,
            preferredCity: user.city || user.preferredCity || user.preferred_city || prev.preferredCity,
            preferredZipCode: user.zipCode || user.zip || user.preferredZipCode || user.preferred_zip_code || prev.preferredZipCode,
          }));
        }
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailCheckError(error.response?.data?.message || 'Error checking email');

      // Hide error message after 5 seconds
      setTimeout(() => {
        setEmailCheckError('');
      }, 5000);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Handle email input blur - trigger email check
  const handleEmailBlur = () => {
    if (inviteFormData.email && isValidEmail(inviteFormData.email)) {
      checkEmail(inviteFormData.email);
    }
  };

  // Roles offered by the invite modal for a transaction that is clearly one side.
  const BUYING_SIDE_PARTY_TYPE_IDS = ['co_buyers_agent', 'transaction_coordinator', 'broker', 'buyer'];
  const LISTING_SIDE_PARTY_TYPE_IDS = ['co_listing_agent', 'transaction_coordinator', 'broker', 'seller'];

  // Filter party types based on transaction type/side and user role
  // Buying-only transactions: Co-Buyer's Agent, Transaction Coordinator, Broker, Buyer
  // Listing-only transactions: Co-Listing Agent, Transaction Coordinator, Broker, Seller
  // Side unknown / ambiguous (e.g. dual): previous rules below are kept unchanged
  const getFilteredPartyTypes = () => {
    const transactionType = transaction?.type?.toUpperCase() || '';
    const transactionSide = transaction?.side?.toUpperCase() || '';
    const userIsAgent = isUserAgent();

    const isListingSide = transactionType === 'SALE' || transactionType === 'LISTING' || transactionSide === 'LISTING';
    const isBuyingSide = transactionType === 'PURCHASE' || transactionType === 'BUYING' || transactionSide === 'BUYING';

    if (isBuyingSide && !isListingSide) {
      return allPartyTypes.filter(type => BUYING_SIDE_PARTY_TYPE_IDS.includes(type.id));
    }
    if (isListingSide && !isBuyingSide) {
      return allPartyTypes.filter(type => LISTING_SIDE_PARTY_TYPE_IDS.includes(type.id));
    }

    return allPartyTypes.filter(type => {
      // For Agent users, hide Listing Agent and Buyer's Agent
      if (userIsAgent && (type.id === 'listing_agent' || type.id === 'buyers_agent')) {
        return false;
      }
      // Co Listing Agent: only for listing/sale transactions
      if (type.id === 'co_listing_agent' && !isListingSide) return false;
      // Co Buying Agent: only for buying/purchase transactions
      if (type.id === 'co_buyers_agent' && !isBuyingSide) return false;
      // For LISTING or SALE transactions, hide Buyer
      if (isListingSide) {
        return type.id !== 'buyer';
      }
      // For PURCHASE or BUYING transactions, hide Seller
      if (isBuyingSide) {
        return type.id !== 'seller';
      }
      return true;
    });
  };

  const partyTypes = getFilteredPartyTypes();

  // Party roles shown first in the Ref Party "Role" select. Same side detection
  // as getFilteredPartyTypes; an unknown/ambiguous side offers both sides.
  const getRefPartyRoles = () => {
    const transactionType = transaction?.type?.toUpperCase() || '';
    const transactionSide = transaction?.side?.toUpperCase() || '';
    const isListingSide = transactionType === 'SALE' || transactionType === 'LISTING' || transactionSide === 'LISTING';
    const isBuyingSide = transactionType === 'PURCHASE' || transactionType === 'BUYING' || transactionSide === 'BUYING';

    if (isBuyingSide && !isListingSide) return REF_PARTY_BUYING_SIDE_ROLES;
    if (isListingSide && !isBuyingSide) return REF_PARTY_LISTING_SIDE_ROLES;
    return [...REF_PARTY_BUYING_SIDE_ROLES, ...REF_PARTY_LISTING_SIDE_ROLES];
  };

  const refPartyRoles = getRefPartyRoles();

  // Keep a stable ref to onParticipantsChange so fetchParticipants doesn't re-create
  // on every parent re-render (which would cause an infinite fetch loop).
  const onParticipantsChangeRef = useRef(onParticipantsChange);
  useEffect(() => { onParticipantsChangeRef.current = onParticipantsChange; }, [onParticipantsChange]);

  // Function to get Firebase token - uses currentUser first, falls back to listener with timeout
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      // If user is already available, get token directly
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      // Otherwise wait for auth state to be ready (with timeout)
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // Helper: get message type from MIME type (matches mobile app logic)
  const getMessageType = (mimeType) => {
    if (!mimeType) return 'text';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'file';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = file.type.startsWith('image/') ? 10 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file.size > maxSize) {
      setSendMessageError(`File too large. Max ${file.type.startsWith('image/') ? '10' : '25'}MB.`);
      setTimeout(() => setSendMessageError(''), 5000);
      e.target.value = '';
      return;
    }
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setPendingAttachment({ file, previewUrl, name: file.name, size: file.size, mimeType: file.type });
    e.target.value = '';
  };

  const removePendingAttachment = () => {
    if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    setPendingAttachment(null);
  };

  // Fetch participants from API
  const fetchParticipants = useCallback(async () => {
    if (!transaction?.id) {
      setIsLoadingParticipants(false);
      return;
    }

    setIsLoadingParticipants(true);
    setParticipantsError(null);

    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.transactionParticipants(transaction.id),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Participants fetched:', response.data);

      // Handle different API response formats
      const data = response.data;
      let normalized = [];
      if (Array.isArray(data)) {
        normalized = data;
      } else if (data && Array.isArray(data.data)) {
        normalized = data.data;
      } else if (data && Array.isArray(data.participants)) {
        normalized = data.participants;
      } else if (data && Array.isArray(data.content)) {
        normalized = data.content;
      }
      setParticipantsData(normalized);
      onParticipantsChangeRef.current?.(normalized);
    } catch (error) {
      console.error('Error fetching participants:', error);
      setParticipantsError('Failed to load parties');
      setParticipantsData([]);
    } finally {
      setIsLoadingParticipants(false);
    }
  }, [transaction?.id, getFirebaseToken]);

  // Fetch profile photos for participants who don't have one
  const fetchParticipantPhotos = useCallback(async (participantsList) => {
    try {
      const firebaseToken = await getFirebaseToken();
      const needsPhoto = participantsList.filter(p => !p.profilePhotoBase64 && !p.profilePhotoUrl && !p.profileImageUrl && !p.photoUrl && p.id);
      if (needsPhoto.length === 0) return;

      const photoResults = await Promise.allSettled(
        needsPhoto.map(async (p) => {
          try {
            // Photo lookup needs the real Rialty user id (p.userId), not the participant
            // row's own id (p.id) — UserService's /users/{id}/photo 404s on a participant
            // id, which silently failed for every registered participant until this fix.
            const response = await apiClient.get(
              API_ENDPOINTS.userPhoto(p.userId || p.id),
              {
                headers: { 'Authorization': `Bearer ${firebaseToken}` },
                responseType: 'blob',
                timeout: 5000
              }
            );
            if (response.data && response.data.size > 0) {
              const photoUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(response.data);
              });
              return { id: p.id, photoUrl };
            }
          } catch {
            // No photo available
          }
          return null;
        })
      );

      const photoMap = {};
      photoResults.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          photoMap[result.value.id] = result.value.photoUrl;
        }
      });

      if (Object.keys(photoMap).length > 0) {
        setParticipantsData(prev => prev.map(p => {
          if (photoMap[p.id]) {
            return { ...p, profilePhotoUrl: photoMap[p.id] };
          }
          return p;
        }));
      }
    } catch (err) {
      console.error('Error fetching participant photos:', err);
    }
  }, [getFirebaseToken]);

  // Fetch participants on component mount
  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // Fetch photos after participants are loaded
  useEffect(() => {
    if (participantsData.length > 0) {
      fetchParticipantPhotos(participantsData);
    }
  }, [participantsData.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the transaction group chat once participants finish loading
  useEffect(() => {
    if (!isLoadingParticipants && transaction?.id && !resolvedTransactionChatId) {
      resolveTransactionChatId(participantsData);
    }
  }, [isLoadingParticipants, transaction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup messages listener on unmount
  useEffect(() => {
    return () => {
      if (messagesListenerRef.current) {
        messagesListenerRef.current();
        messagesListenerRef.current = null;
      }
    };
  }, []);

  // Auto-select group chat once resolvedTransactionChatId is available
  useEffect(() => {
    if (resolvedTransactionChatId && !selectedParticipant && !isGroupChatSelected) {
      setIsGroupChatSelected(true);
      subscribeToMessages(resolvedTransactionChatId);
    }
  }, [resolvedTransactionChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle group chat selection
  const handleGroupChatSelect = async () => {
    setIsGroupChatSelected(true);
    setSelectedParticipant(null);
    setChatMessages([]);
    setSendMessageError('');
    setLinkPreview(null);
    lastDetectedUrlRef.current = null;
    linkPreviewDismissedRef.current = false;
    let chatId = resolvedTransactionChatId;
    if (!chatId) {
      chatId = await resolveTransactionChatId(participantsData);
    }
    if (chatId) subscribeToMessages(chatId);
  };

  // Generate avatar color based on name
  const getAvatarColor = (name) => {
    const colors = ['#5B47D6', '#00C092', '#FF9500', '#FF6B6B', '#4ECDC4', '#F59E0B', '#E91E63', '#9C27B0', '#2196F3', '#4CAF50'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Get initials from name
  const getInitials = (firstName, lastName, fullName) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (fullName) {
      const parts = fullName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return fullName.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  // Find chatId from participant data by scanning all fields
  const findChatId = (participant) => {
    // Check known field names first
    const knownFields = ['chatId', 'conversationId', 'chatRoomId', 'chatConversationId', 'directChatId'];
    for (const field of knownFields) {
      if (participant[field]) return participant[field];
    }
    // Check nested chat object
    if (participant.chat?.id) return participant.chat.id;
    // Scan all keys for anything containing 'chat' or 'conversation' with a string value
    for (const key of Object.keys(participant)) {
      if ((key.toLowerCase().includes('chat') || key.toLowerCase().includes('conversation')) && typeof participant[key] === 'string' && participant[key].length > 0) {
        return participant[key];
      }
    }
    return null;
  };

  // Format participant data — spread raw data to preserve all API fields
  const formatParticipant = (participant) => {
    const fullName = participant.name ||
      `${participant.firstName || ''} ${participant.lastName || ''}`.trim() ||
      participant.email || 'Unknown';

    return {
      ...participant, // Preserve ALL raw fields from API
      id: participant.id || participant.odooId || participant.email,
      name: fullName,
      initials: getInitials(participant.firstName, participant.lastName, fullName),
      role: (participant.role || participant.participantRole || participant.type || 'Participant').replaceAll('_', ' '),
      roleLabel: formatRoleLabel(participant.role || participant.participantRole || participant.type),
      avatarColor: getAvatarColor(fullName),
      phone: participant.phone || participant.phoneNumber || '',
      email: participant.email || '',
      status: participant.status || 'active',
      invitationStatus: participant.invitationStatus || participant.inviteStatus || null,
      chatId: findChatId(participant),
      firebaseUid: participant.firebaseUid || participant.uid || null,
      profilePhotoUrl: participant.profilePhotoBase64 || participant.profilePhotoUrl || participant.profileImageUrl || participant.photoUrl || null,
    };
  };

  // Separate participants into clients (buyer/seller) and team (others)
  const getParticipants = () => {
    const clientRoles = ['buyer', 'seller'];

    const clients = participantsData
      .filter(p => {
        const role = (p.role || p.participantRole || p.type || '').toLowerCase();
        return clientRoles.includes(role);
      })
      .map(formatParticipant);

    const team = participantsData
      .filter(p => {
        const role = (p.role || p.participantRole || p.type || '').toLowerCase();
        return !clientRoles.includes(role);
      })
      .map(formatParticipant);

    return { clients, team };
  };

  const participants = getParticipants();

  // Get current user UID
  const getCurrentUid = () => auth.currentUser?.uid || '';

  // Format timestamp for messages
  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    const msgDate = new Date(dateStr);
    const now = new Date();
    const diffMs = now - msgDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const zone = getViewerTimezone();
    if (diffDays === 0) {
      return msgDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    }
    return msgDate.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: zone });
  };

  // Subscribe to real-time messages via Firestore — mirrors mobile subscribeToMessages.
  // Firestore fires instantly when any participant sends a message; decrypted content
  // is fetched from the REST API (backend decrypts before returning).
  const subscribeToMessages = useCallback((chatId) => {
    // Clean up previous listener
    if (messagesListenerRef.current) {
      messagesListenerRef.current();
      messagesListenerRef.current = null;
    }
    if (!chatId) return;

    decryptedCacheRef.current = new Map();
    setIsLoadingMessages(true);

    const decryptedCache = decryptedCacheRef.current;
    let isFetchingDecrypted = false;

    const fetchDecryptedContent = async () => {
      if (isFetchingDecrypted) return;
      isFetchingDecrypted = true;
      try {
        const firebaseToken = await getFirebaseToken();
        if (!firebaseToken) return;
        const response = await apiClient.get(
          API_ENDPOINTS.chatMessages(chatId),
          { headers: { 'Authorization': `Bearer ${firebaseToken}` }, params: { limit: 100 }, timeout: 10000 }
        );
        const msgs = Array.isArray(response.data) ? response.data
          : Array.isArray(response.data?.messages) ? response.data.messages
          : Array.isArray(response.data?.data) ? response.data.data
          : [];
        msgs.forEach(msg => {
          const id = msg.messageId || msg.id;
          if (id && msg.content) decryptedCache.set(id, msg.content);
        });
      } catch (err) {
        console.warn('[Parties Chat] Error fetching decrypted messages:', err?.message);
      } finally {
        isFetchingDecrypted = false;
      }
    };

    const currentUid = getCurrentUid();
    const msgsQuery = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(msgsQuery, async (snapshot) => {
      const hasNew = snapshot.docs.some(d => !decryptedCache.has(d.id));
      if (hasNew) await fetchDecryptedContent();

      const msgs = snapshot.docs
        .filter(d => {
          const data = d.data();
          return !data.deletedForEveryone && !(currentUid && data.deletedBy?.includes(currentUid));
        })
        .map(d => {
          const data = d.data();
          const content = decryptedCache.get(d.id) || data.content || '';
          const ts = data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : null);
          const isoTs = ts?.toISOString() || '';
          return {
            id: d.id,
            text: content,
            senderId: data.senderId || '',
            senderName: data.senderName || '',
            createdAt: isoTs,
            sent: data.senderId === currentUid,
            time: formatMessageTime(isoTs),
            messageType: (data.messageType || 'text').toLowerCase(),
            propertyData: data.metadata?.propertyData || data.propertyData || null,
            attachments: data.metadata?.attachments || data.attachments || [],
            linkPreview: data.metadata?.linkPreview || null,
          };
        });

      setChatMessages(msgs);
      setIsLoadingMessages(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }, (err) => {
      console.error('[Parties Chat] Messages listener error:', err);
      setIsLoadingMessages(false);
    });

    messagesListenerRef.current = unsubscribe;
  }, [getFirebaseToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the transaction group chat ID.
  // The mobile app uses ONE group chat per transaction, stored in Firestore with dealId == transactionId.
  // We scan the user's own chat list (user_chats/{uid}/chats) — same approach as ConversationPage —
  // checking each chat document's dealId field. If not found, create via API.
  const resolveTransactionChatId = useCallback(async (participantsList) => {
    if (!transaction?.id) return null;

    const currentUid = getCurrentUid();
    if (!currentUid) return null;

    const transactionIdStr = String(transaction.id);

    // ── Step 1: query chats collection directly by dealId (same as mobile) ────
    try {
      console.log('[Chat] Querying chats collection for dealId:', transactionIdStr);
      const chatsQuery = query(
        collection(db, 'chats'),
        where('dealId', '==', transactionIdStr),
        limit(1)
      );
      const chatsSnapshot = await getDocs(chatsQuery);
      if (!chatsSnapshot.empty) {
        const chatId = chatsSnapshot.docs[0].id;
        console.log('[Chat] Found transaction chat via direct query:', chatId);
        setResolvedTransactionChatId(chatId);
        return chatId;
      }
      console.log('[Chat] No matching dealId found in chats collection');
    } catch (err) {
      console.warn('[Chat] Error querying chats collection:', err?.message);
    }

    // ── Step 1b: fallback — scan user_chats/{uid}/chats subcollection ─────────
    try {
      console.log('[Chat] Falling back to user_chats scan for dealId:', transactionIdStr);
      const userChatsRef = collection(db, 'user_chats', currentUid, 'chats');
      const userChatsSnapshot = await getDocs(userChatsRef);

      for (const chatRefDoc of userChatsSnapshot.docs) {
        const chatRefData = chatRefDoc.data();
        const chatId = chatRefData.chatId || chatRefDoc.id;
        try {
          const chatDocRef = doc(db, 'chats', chatId);
          const chatDoc = await getDoc(chatDocRef);
          if (!chatDoc.exists()) continue;

          const chatData = chatDoc.data();
          const dealId = String(chatData.dealId || '');
          if (dealId && dealId === transactionIdStr) {
            console.log('[Chat] Found transaction chat via user_chats scan:', chatId);
            setResolvedTransactionChatId(chatId);
            return chatId;
          }
        } catch {
          // can't fetch this specific chat doc — continue to next
        }
      }
      console.log('[Chat] No matching dealId found in user_chats');
    } catch (err) {
      console.warn('[Chat] Error scanning user_chats:', err?.message);
    }

    // ── Step 3: create (or fetch existing) via POST /api/v1/chats ────────────
    // Note: server returns existing chat if one already has this dealId,
    // so this is safe to call even when a chat already exists.
    try {
      console.log('[Chat] Creating transaction chat via API...');
      const firebaseToken = await getFirebaseToken();

      // Collect participant Firebase UIDs from formatted participants list
      const participantUids = (participantsList || [])
        .filter(p => p.firebaseUid)
        .map(p => p.firebaseUid);

      // Always include current user
      if (!participantUids.includes(currentUid)) {
        participantUids.unshift(currentUid);
      }

      const response = await apiClient.post(
        API_ENDPOINTS.createChat,
        {
          participants: participantUids,
          chatName: transaction.name || transaction.propertyAddress || 'Transaction Chat',
          dealId: transactionIdStr,
        },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('[Chat] Create chat API response:', JSON.stringify(response.data));
      const chatId = response.data?.chatId
        || response.data?.data?.chatId
        || response.data?.id
        || response.data?.data?.id;

      if (chatId) {
        console.log('[Chat] Transaction chat created/found via API:', chatId);
        setResolvedTransactionChatId(chatId);
        return chatId;
      }
    } catch (err) {
      console.error('[Chat] Error creating transaction chat via API:',
        err?.response?.status, JSON.stringify(err?.response?.data), err?.message);
    }

    return null;
  }, [transaction?.id, transaction?.chatId, transaction?.name, transaction?.propertyAddress, getFirebaseToken]);

  // Resolve a private direct (1-on-1) chat between the current user and one participant.
  // Scans the current user's user_chats subcollection for an existing 2-person chat
  // (no dealId — that would be the group chat). Creates via API if not found.
  const resolveDirectChatId = useCallback(async (participant) => {
    const currentUid = getCurrentUid();
    if (!currentUid) return null;

    // ── Step 1: get the participant's Firebase UID ────────────────────────────
    let participantUid = participant.firebaseUid;

    if (!participantUid && participant.id) {
      try {
        console.log('[DirectChat] Fetching firebaseUid for participant:', participant.name);
        const firebaseToken = await getFirebaseToken();
        const resp = await apiClient.get(
          API_ENDPOINTS.userById(participant.id),
          { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 8000 }
        );
        const userData = resp.data?.data || resp.data;
        participantUid = userData?.firebaseUid || userData?.uid || null;
        console.log('[DirectChat] Resolved firebaseUid:', participantUid);
      } catch (err) {
        console.warn('[DirectChat] Could not fetch participant firebaseUid:', err?.message);
      }
    }

    if (!participantUid) {
      console.warn('[DirectChat] No firebaseUid available for', participant.name);
      return null;
    }

    // ── Step 2: scan user_chats for an existing 1-on-1 chat ──────────────────
    try {
      const userChatsRef = collection(db, 'user_chats', currentUid, 'chats');
      const userChatsSnapshot = await getDocs(userChatsRef);

      for (const chatRefDoc of userChatsSnapshot.docs) {
        const chatRefData = chatRefDoc.data();
        const chatId = chatRefData.chatId || chatRefDoc.id;
        try {
          const chatDocRef = doc(db, 'chats', chatId);
          const chatDoc = await getDoc(chatDocRef);
          if (!chatDoc.exists()) continue;

          const chatData = chatDoc.data();
          const chatParticipants = chatData.participants || [];

          // A direct chat: exactly 2 members, both the current user and the target, no dealId
          if (
            chatParticipants.length === 2 &&
            chatParticipants.includes(currentUid) &&
            chatParticipants.includes(participantUid) &&
            !chatData.dealId
          ) {
            console.log('[DirectChat] Found existing 1-on-1 chat:', chatId);
            return chatId;
          }
        } catch {
          // skip this chat doc
        }
      }
      console.log('[DirectChat] No existing 1-on-1 chat found, will create');
    } catch (err) {
      console.warn('[DirectChat] Error scanning user_chats:', err?.message);
    }

    // ── Step 3: create a new direct chat ─────────────────────────────────────
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.post(
        API_ENDPOINTS.createChat,
        { participants: [currentUid, participantUid] },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );

      console.log('[DirectChat] Create response:', JSON.stringify(response.data));
      const chatId = response.data?.chatId
        || response.data?.data?.chatId
        || response.data?.id
        || response.data?.data?.id;

      if (chatId) {
        console.log('[DirectChat] Created 1-on-1 chat:', chatId);
        return chatId;
      }
    } catch (err) {
      console.error('[DirectChat] Error creating 1-on-1 chat:',
        err?.response?.status, JSON.stringify(err?.response?.data), err?.message);
    }

    return null;
  }, [getFirebaseToken]);

  // Returns true if the participant is the currently logged-in user
  const isCurrentUser = (participant) => {
    const uid = getCurrentUid();
    if (uid && participant.firebaseUid && participant.firebaseUid === uid) return true;
    const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    if (storedUser.email && participant.email && participant.email.toLowerCase() === storedUser.email.toLowerCase()) return true;
    if (storedUser.id && participant.id && String(participant.id) === String(storedUser.id)) return true;
    return false;
  };

  // Handle participant click — resolve a private 1-on-1 chat with that person
  const handleParticipantSelect = async (participant) => {
    // Never open a chat with yourself — no API should be called
    if (isCurrentUser(participant)) return;

    // Skip if already selected and chat already resolved
    if (selectedParticipant?.id === participant.id && !isGroupChatSelected && selectedParticipant?.resolvedChatId) return;

    console.log('[Parties] Selected participant:', participant.name);
    setIsGroupChatSelected(false);
    setSelectedParticipant(participant);
    setChatMessages([]);
    setSendMessageError('');

    // Resolve private 1-on-1 chat for this participant
    const chatId = await resolveDirectChatId(participant);
    if (chatId) {
      setSelectedParticipant(prev => ({ ...prev, resolvedChatId: chatId }));
      subscribeToMessages(chatId);
    } else {
      console.warn('[Parties] Could not resolve direct chat for:', participant.name);
      setSendMessageError('Chat not available for this participant yet.');
      setTimeout(() => setSendMessageError(''), 4000);
    }
  };

  // Get total participant count
  const getTotalParticipants = () => {
    return participants.clients.length + participants.team.length;
  };

  // Filter participants based on search
  const filterParticipants = (list) => {
    if (!searchQuery) return list;
    return list.filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.role.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Get transaction address
  const getTransactionAddress = () => {
    if (!transaction) return '';
    return transaction.propertyAddress || '2450 Ashley Ave';
  };

  // Get full address for modal
  const getFullAddress = () => {
    if (!transaction) return '2450 Ashley Ave, Los Angeles, CA 90210';
    const parts = [
      transaction.propertyAddress,
      transaction.propertyCity,
      transaction.propertyState,
      transaction.propertyZip
    ].filter(Boolean);
    return parts.join(', ') || '2450 Ashley Ave, Los Angeles, CA 90210';
  };

  // Handle text change with URL detection — fetches real OG tags (title, image, description)
  const handleTextChange = (text) => {
    setMessageText(text);

    if (linkPreviewTimeoutRef.current) clearTimeout(linkPreviewTimeoutRef.current);

    const urlMatch = text.match(/https?:\/\/[^\s]+/);
    const detectedUrl = urlMatch ? urlMatch[0] : null;

    if (!detectedUrl) {
      if (linkPreview && !text.includes(linkPreview.url)) {
        setLinkPreview(null);
        setIsFetchingPreview(false);
        lastDetectedUrlRef.current = null;
        linkPreviewDismissedRef.current = false;
        propertyPreviewRef.current = null;
      }
      return;
    }

    if (detectedUrl !== lastDetectedUrlRef.current) linkPreviewDismissedRef.current = false;
    if (detectedUrl === lastDetectedUrlRef.current && linkPreview) return;

    linkPreviewTimeoutRef.current = setTimeout(async () => {
      if (linkPreviewDismissedRef.current) return;
      lastDetectedUrlRef.current = detectedUrl;
      setIsFetchingPreview(true);
      setLinkPreview(null);
      try {
        const preview = await fetchLinkPreview(detectedUrl);
        if (!linkPreviewDismissedRef.current && preview) {
          setLinkPreview(preview);
        }

        // ATTOM address lookup — runs while isFetchingPreview is still true so the send
        // button stays disabled until property data is ready (mirrors mobile app behaviour).
        // For short URLs (e.g. redf.in/xxx), resolve to canonical first so
        // parseStructuredAddress can extract the address, and store the canonical URL
        // as sourceUrl so the Selected Properties tab shows the correct source label.
        let parsed = parseStructuredAddress(detectedUrl);
        let sourceUrl = detectedUrl;
        if (!parsed) {
          const canonical = await resolveShortUrl(detectedUrl);
          if (canonical !== detectedUrl) {
            parsed = parseStructuredAddress(canonical);
            if (parsed) sourceUrl = canonical;
          }
        }
        resolvedSourceUrlRef.current = sourceUrl;

        if (parsed) {
          try {
            const token = await getFirebaseToken();
            if (token) {
              const address1 = parsed.street;
              let address2 = `${parsed.state} ${parsed.zip}`.trim();
              if (parsed.city) address2 = `${parsed.city}, ${address2}`;
              const attomResp = await apiClient.get(API_ENDPOINTS.attomAddressLookup, {
                headers: { 'Authorization': `Bearer ${token}` },
                params: { address1, address2 },
                timeout: 10000,
              });
              const d = attomResp.data;
              if (d) {
                propertyPreviewRef.current = {
                  mlsId: d.mlsId || '',
                  price: d.listPrice || 0,
                  address: d.streetAddress || d.fullAddress || '',
                  city: d.city || '',
                  state: d.state || '',
                  zipCode: d.postalCode || '',
                  beds: d.bedrooms || 0,
                  baths: d.bathrooms || 0,
                  sqft: d.squareFeet || null,
                  primaryPhoto: d.primaryPhoto || null,
                  ownerName: d.ownerName || null,
                };
              }
            }
          } catch {
            propertyPreviewRef.current = null;
          }
        } else {
          propertyPreviewRef.current = null;
        }
      } finally {
        setIsFetchingPreview(false);
      }
    }, 500);
  };

  const handleDismissLinkPreview = () => {
    setLinkPreview(null);
    setIsFetchingPreview(false);
    if (linkPreviewTimeoutRef.current) clearTimeout(linkPreviewTimeoutRef.current);
    linkPreviewDismissedRef.current = true;
    propertyPreviewRef.current = null;
    resolvedSourceUrlRef.current = null;
  };

  // Render message text with clickable URLs
  const renderMessageText = (text) => {
    if (!text) return null;
    const urlRegex = /https?:\/\/[^\s]+/g;
    const parts = text.split(urlRegex);
    const urls = text.match(urlRegex) || [];
    return parts.reduce((result, part, i) => {
      if (part) result.push(<span key={`t-${i}`}>{part}</span>);
      if (urls[i]) {
        result.push(
          <a key={`u-${i}`} href={urls[i]} target="_blank" rel="noopener noreferrer" className="cp-message-link">
            {urls[i]}
          </a>
        );
      }
      return result;
    }, []);
  };

  // Extract the numeric property ID from a real estate listing URL.
  // Used in Path B (no ATTOM match) to avoid passing the full URL as mlsId
  // which causes a 500 on the /interested-properties/check/{mlsId} endpoint.
  const extractPropertyIdFromUrl = (url) => {
    // Zillow: /homedetails/.../337097114_zpid/
    const zpid = url.match(/\/(\d+)_zpid/);
    if (zpid) return zpid[1];
    // Redfin: /home/65946698
    const redfin = url.match(/\/home\/(\d+)/i);
    if (redfin) return redfin[1];
    // Realtor.com: ends with /M{id} or trailing numeric segment
    const realtor = url.match(/realtor\.com[^?#]*[/-](\d{6,})/i);
    if (realtor) return realtor[1];
    // Trulia/Homes.com: trailing -ID before slash/end
    const generic = url.match(/[/-](\d{6,})(?:\/|$|\?)/);
    if (generic) return generic[1];
    return null;
  };

  const handleSendMessage = async () => {
    const text = messageText.trim();
    if (!text && !pendingAttachment) return;
    if (!isGroupChatSelected && !selectedParticipant) return;

    const currentUid = getCurrentUid();
    const currentLinkPreview = linkPreview;
    const capturedAttachment = pendingAttachment;
    const capturedPropertyPreview = propertyPreviewRef.current;
    const capturedSourceUrl = resolvedSourceUrlRef.current;

    // Clear input immediately — same pattern as ConversationPage (no blocking state)
    setMessageText('');
    setLinkPreview(null);
    setIsFetchingPreview(false);
    setPendingAttachment(null);
    lastDetectedUrlRef.current = null;
    resolvedSourceUrlRef.current = null;
    linkPreviewDismissedRef.current = false;
    propertyPreviewRef.current = null;
    if (linkPreviewTimeoutRef.current) clearTimeout(linkPreviewTimeoutRef.current);
    setSendMessageError('');

    // Optimistic UI — show message instantly before any async work
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      text,
      senderId: currentUid,
      sent: true,
      linkPreview: currentLinkPreview,
      attachments: capturedAttachment ? [{ fileName: capturedAttachment.name, mimeType: capturedAttachment.mimeType, previewUrl: capturedAttachment.previewUrl, fileSize: capturedAttachment.size }] : [],
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
    setChatMessages(prev => [...prev, optimisticMsg]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    try {
      const firebaseToken = await getFirebaseToken();

      // Resolve chatId — usually already cached so no visible delay
      let chatId;
      if (isGroupChatSelected) {
        chatId = resolvedTransactionChatId;
        if (!chatId) chatId = await resolveTransactionChatId(participantsData);
      } else {
        chatId = selectedParticipant?.resolvedChatId;
        if (!chatId) {
          chatId = await resolveDirectChatId(selectedParticipant);
          if (chatId) setSelectedParticipant(prev => ({ ...prev, resolvedChatId: chatId }));
        }
      }

      if (!chatId) {
        setChatMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
        setMessageText(text);
        if (capturedAttachment) setPendingAttachment(capturedAttachment);
        setSendMessageError('Chat is not ready yet. Please try again in a moment.');
        setTimeout(() => setSendMessageError(''), 5000);
        return;
      }

      console.log('[Parties Chat] chatId for send:', chatId, isGroupChatSelected ? '(group)' : `(direct to ${selectedParticipant?.name})`);

      // Upload attachment first if present
      let attachments = [];
      let messageType = 'text';

      if (capturedAttachment) {
        setIsUploadingAttachment(true);
        const formData = new FormData();
        formData.append('file', capturedAttachment.file);
        const uploadResponse = await apiClient.post(
          API_ENDPOINTS.chatAttachments(chatId),
          formData,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              // Explicitly unset the JSON default so the browser can generate
              // the multipart Content-Type itself (with the required boundary
              // param). Setting 'multipart/form-data' here directly leaves the
              // boundary off and the backend fails to parse the body.
              'Content-Type': undefined,
            },
            timeout: 60000,
          }
        );
        setIsUploadingAttachment(false);
        attachments = [{
          fileName: uploadResponse.data.fileName || capturedAttachment.name,
          fileUrl: uploadResponse.data.fileUrl,
          fileSize: uploadResponse.data.fileSize || capturedAttachment.size,
          mimeType: uploadResponse.data.mimeType || capturedAttachment.mimeType,
        }];
        messageType = getMessageType(attachments[0].mimeType);
      }

      // ── Interested-properties flow (runs BEFORE chat message send) ──────────
      const realEstateDomains = /zillow\.com|redfin\.com|realtor\.com|trulia\.com|homes\.com/i;
      let propertyPayload = null; // set when message should be sent as messageType='property'

      if (capturedPropertyPreview && transaction?.id) {
        // Path A: ATTOM lookup succeeded — full property data available
        const mlsIdStr = capturedPropertyPreview.mlsId?.toString() || '';
        if (mlsIdStr) {
          try {
            console.log('[InterestedProperties] Checking duplicate for mlsId:', mlsIdStr);
            const checkResp = await apiClient.get(
              API_ENDPOINTS.checkInterestedProperty(transaction.id, mlsIdStr),
              { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 10000 }
            );
            if (!checkResp.data?.exists) {
              console.log('[InterestedProperties] Adding new property:', mlsIdStr);
              await apiClient.post(
                API_ENDPOINTS.interestedProperties(transaction.id),
                {
                  mlsId: mlsIdStr,
                  propertyAddress: capturedPropertyPreview.address,
                  propertyCity: capturedPropertyPreview.city,
                  propertyState: capturedPropertyPreview.state,
                  propertyZip: capturedPropertyPreview.zipCode,
                  listPrice: capturedPropertyPreview.price,
                  bedrooms: capturedPropertyPreview.beds,
                  bathrooms: capturedPropertyPreview.baths,
                  squareFeet: capturedPropertyPreview.sqft,
                  primaryPhoto: capturedPropertyPreview.primaryPhoto,
                  sellerName: capturedPropertyPreview.ownerName,
                  sourceUrl: capturedSourceUrl || undefined,
                },
                { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 10000 }
              );
            } else {
              console.log('[InterestedProperties] Property already exists, skipping add');
            }
          } catch {
            // Silent — not critical if this fails
          }
          propertyPayload = capturedPropertyPreview;
          messageType = 'property';
        }
      } else if (!capturedPropertyPreview && transaction?.id) {
        // Path B: No ATTOM match — use canonical URL (capturedSourceUrl from resolveShortUrl,
        // or currentLinkPreview.url which fetchLinkPreview already resolved for short links).
        const effectiveUrl = capturedSourceUrl || currentLinkPreview?.url;
        if (effectiveUrl && realEstateDomains.test(effectiveUrl)) {
          const titleParts = (currentLinkPreview?.title || '').split('|')[0].trim();
          // Extract numeric property ID — never pass the full URL as mlsId (causes 500 on check endpoint)
          const mlsId = extractPropertyIdFromUrl(effectiveUrl);
          const stateZipMatch = titleParts.match(/,\s*([A-Z]{2})\s+(\d{5})/);
          const cityMatch = titleParts.match(/,\s*([^,]+),\s*[A-Z]{2}\s+\d{5}/);
          const desc = currentLinkPreview?.description || '';
          const priceRaw = (desc.match(/\$[\d,]+/) || currentLinkPreview?.title?.match(/\$[\d,]+/) || [])[0];
          const bedsMatch = desc.match(/(\d+)\s*bed/i);
          const bathsMatch = desc.match(/(\d+)\s*bath/i);
          const sqftMatch = desc.match(/([\d,]+)\s*square\s*feet/i);

          const fallbackProperty = {
            id: mlsId,
            mlsId: parseInt(mlsId, 10) || 0,
            price: priceRaw ? parseInt(priceRaw.replace(/[$,]/g, ''), 10) : 0,
            address: titleParts.replace(/,\s*[A-Z]{2}\s+\d{5}.*/, '').trim(),
            city: cityMatch ? cityMatch[1].trim() : '',
            state: stateZipMatch ? stateZipMatch[1] : '',
            zipCode: stateZipMatch ? stateZipMatch[2] : '',
            beds: bedsMatch ? parseInt(bedsMatch[1], 10) : 0,
            baths: bathsMatch ? parseInt(bathsMatch[1], 10) : 0,
            sqft: sqftMatch ? parseInt(sqftMatch[1].replace(/,/g, ''), 10) : null,
            primaryPhoto: currentLinkPreview?.imageUrl || null,
          };

          if (mlsId) {
            try {
              console.log('[InterestedProperties] Path B — checking duplicate for mlsId:', mlsId);
              const checkResp = await apiClient.get(
                API_ENDPOINTS.checkInterestedProperty(transaction.id, mlsId),
                { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 10000 }
              );
              if (!checkResp.data?.exists) {
                console.log('[InterestedProperties] Path B — adding property from link preview:', mlsId);
                await apiClient.post(
                  API_ENDPOINTS.interestedProperties(transaction.id),
                  {
                    mlsId: mlsId,
                    propertyAddress: fallbackProperty.address,
                    propertyCity: fallbackProperty.city,
                    propertyState: fallbackProperty.state,
                    propertyZip: fallbackProperty.zipCode,
                    listPrice: fallbackProperty.price,
                    bedrooms: fallbackProperty.beds,
                    bathrooms: fallbackProperty.baths,
                    squareFeet: fallbackProperty.sqft,
                    primaryPhoto: currentLinkPreview?.imageUrl || null,
                    sourceUrl: effectiveUrl,
                  },
                  { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 10000 }
                );
              } else {
                console.log('[InterestedProperties] Path B — property already exists, skipping add');
              }
            } catch {
              // Silent — not critical if this fails
            }
          }
          // Only treat as a property message if we could extract a usable ID
          if (mlsId) {
            propertyPayload = fallbackProperty;
            messageType = 'property';
          }
        }
      }
      // ── End interested-properties flow ──────────────────────────────────────

      // Build message body based on path
      let messageBody;
      if (propertyPayload) {
        // Property message — messageType='property' with metadata.propertyData (same as mobile sendPropertyMessage)
        messageBody = {
          content: text || '',
          messageType: 'property',
          metadata: {
            propertyData: {
              id: propertyPayload.id || propertyPayload.mlsId?.toString(),
              mlsId: propertyPayload.mlsId,
              price: propertyPayload.price,
              address: propertyPayload.address,
              city: propertyPayload.city,
              state: propertyPayload.state,
              zipCode: propertyPayload.zipCode,
              beds: propertyPayload.beds,
              baths: propertyPayload.baths,
              sqft: propertyPayload.sqft,
              primaryPhoto: propertyPayload.primaryPhoto,
              status: propertyPayload.status,
            },
          },
        };
      } else {
        // Regular text / attachment message
        messageBody = {
          content: text || (attachments.length > 0 ? attachments[0].fileName : ''),
          messageType,
          attachments,
          ...(currentLinkPreview ? { metadata: { linkPreview: currentLinkPreview } } : {}),
        };
      }

      await apiClient.post(
        API_ENDPOINTS.chatMessages(chatId),
        messageBody,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      // Firestore listener picks up the new message automatically — no manual re-fetch needed
    } catch (err) {
      setIsUploadingAttachment(false);
      console.error('[Parties Chat] Error sending message:', err?.response?.status, JSON.stringify(err?.response?.data), err?.message);
      // Remove optimistic message and restore input/attachment
      setChatMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
      setMessageText(text);
      if (capturedAttachment) setPendingAttachment(capturedAttachment);
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to send message. Please try again.';
      setSendMessageError(errMsg);
      setTimeout(() => setSendMessageError(''), 5000);
    }
  };

  // Reset form data
  const getEmptyFormData = () => ({
    email: '',
    firstName: '',
    middleName: '',
    lastName: '',
    countryCode: '+1',
    phone: '',
    company: '',
    preferredAddress: '',
    preferredState: '',
    preferredCity: '',
    preferredZipCode: '',
    address: '',
    state: '',
    city: '',
    zipCode: '',
    notes: ''
  });

  const handleInviteParty = () => {
    setShowInvitePartyModal(true);
    setSelectedPartyType(null);
    setInviteStep(1);
    setInviteFormData(getEmptyFormData());

    // Reset email check states
    setEmailCheckResult(null);
    setEmailCheckError('');
    setIsCheckingEmail(false);
  };

  const fetchRefPartyServiceTypes = async () => {
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.get(API_ENDPOINTS.serviceTypes, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
      if (catalog.length > 0) {
        setRefPartyServiceTypes(catalog.map(t => ({ id: t.id, name: t.label })));
      }
    } catch (error) {
      // Allow a retry on the next open; the fallback list stays usable meanwhile.
      refPartyServiceTypesFetchedRef.current = false;
      console.warn('[RefParty] Failed to fetch service types, using fallback:', error);
    }
  };

  const handleOpenRefPartyModal = () => {
    setRefPartyForm({ email: '', firstName: '', middleName: '', lastName: '', phone: '', role: '' });
    setRefPartyError('');
    setRefEmailChecking(false);
    setRefEmailChecked(false);
    setRefExistingUser(null);
    setRefEmailStatusMessage('');
    setShowRefPartyModal(true);
    if (!refPartyServiceTypesFetchedRef.current) {
      refPartyServiceTypesFetchedRef.current = true;
      fetchRefPartyServiceTypes();
    }
  };

  const handleCloseRefPartyModal = () => {
    if (isAddingRefParty) return;
    setShowRefPartyModal(false);
    setRefPartyError('');
    setRefEmailChecking(false);
    setRefEmailChecked(false);
    setRefExistingUser(null);
    setRefEmailStatusMessage('');
  };

  // Run the email-exists check and populate the "found" state for the UI's
  // branch. Idempotent — called from the Check button and from the submit
  // flow as a safety net when the user never explicitly clicked Check. On
  // any failure we clear the found state and surface the error so the
  // caller doesn't silently fall through to invite-send on an unreliable
  // lookup (fail-closed, matching the backend inviteToPlatform pattern).
  const runRefPartyEmailCheck = async () => {
    const email = refPartyForm.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setRefEmailStatusMessage('');
      setRefEmailChecked(false);
      setRefExistingUser(null);
      return null;
    }
    setRefEmailChecking(true);
    setRefEmailStatusMessage('');
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.post(
        API_ENDPOINTS.checkEmail,
        { email },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } },
      );
      const data = response.data || {};
      setRefEmailChecked(true);
      if (data.exists) {
        const found = {
          userId: data.userId,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          phone: data.phone,
          email: data.email || email,
        };
        setRefExistingUser(found);
        // Auto-fill the name/phone from the matched user so the agent sees
        // what will land on the row; stays editable in case they want a
        // different display label.
        setRefPartyForm(prev => ({
          ...prev,
          firstName: found.firstName || prev.firstName,
          middleName: found.middleName || prev.middleName,
          lastName: found.lastName || prev.lastName,
          phone: found.phone || prev.phone,
        }));
        setRefEmailStatusMessage('');
        return found;
      }
      setRefExistingUser(null);
      return null;
    } catch (err) {
      setRefEmailChecked(false);
      setRefExistingUser(null);
      setRefEmailStatusMessage(err?.response?.data?.message || err?.message || 'Could not verify email. Please try again.');
      return undefined;  // signals failure to callers
    } finally {
      setRefEmailChecking(false);
    }
  };

  const handleAddRefPartySubmit = async () => {
    const first = refPartyForm.firstName.trim();
    const middle = refPartyForm.middleName.trim();
    const last = refPartyForm.lastName.trim();
    const email = refPartyForm.email.trim().toLowerCase();

    // Email is required for new ref parties — the whole flow pivots on the
    // check-email branch. Format-only validation here; the Check call
    // surfaces found-user vs not-found semantics.
    if (!email) {
      setRefPartyError('Email is required for a reference party.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setRefPartyError('Please enter a valid email address.');
      return;
    }

    if (!first || !last) {
      setRefPartyError('First name and last name are required.');
      return;
    }

    if (!refPartyForm.role) {
      setRefPartyError('Please select a role.');
      return;
    }

    // If the user typed an email but never clicked Check (or edited it after
    // checking), run the check now so we know which branch to take. A failed
    // check (undefined return) aborts — we don't want to silently invite an
    // existing user because the lookup glitched.
    let foundUser = refExistingUser;
    if (email && !refEmailChecked) {
      foundUser = await runRefPartyEmailCheck();
      if (foundUser === undefined) {
        return;  // lookup failed; error already surfaced in refEmailStatusMessage
      }
    }

    const name = [first, middle, last].filter(Boolean).join(' ');

    let role = refPartyForm.role;
    let metadata;
    if (role.startsWith(REF_PARTY_SERVICE_TYPE_PREFIX)) {
      const serviceTypeId = role.slice(REF_PARTY_SERVICE_TYPE_PREFIX.length);
      const serviceType = refPartyServiceTypes.find(t => String(t.id) === serviceTypeId);
      role = 'SERVICE_PROVIDER';
      metadata = { serviceType: serviceTypeId, serviceTypeLabel: serviceType?.name || serviceTypeId };
    }

    setIsAddingRefParty(true);
    setRefPartyError('');

    try {
      const firebaseToken = await getFirebaseToken();
      // Three branches, same semantics as mobile PartiesTab:
      //   A. No email → plain ref-party contact row, no invite sent.
      //   B. Email + found existing Rialty user → row with userId linked by
      //      the backend; platformInviteStatus=ALREADY_MEMBER stamped
      //      server-side so the "On Rialty" badge renders.
      //   C. Email + no match → create row, then follow up with
      //      /invite-to-platform in a second call.
      const addResp = await apiClient.post(
        API_ENDPOINTS.transactionParticipants(transaction.id),
        {
          name,
          ...(email && { email }),
          ...(refPartyForm.phone.trim() && { phone: toE164US(refPartyForm.phone.trim()) }),
          role,
          referenceOnly: true,
          ...(metadata && { metadata }),
        },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      // Branch C: fire the platform invite. Best-effort — the row is already
      // a legitimate ref-party; a failed invite is recoverable via the
      // row's "Invite to Rialty" icon.
      const participantId = addResp?.data?.id;
      if (email && !foundUser && participantId) {
        try {
          await apiClient.post(
            API_ENDPOINTS.participantInviteToPlatform(transaction.id, participantId),
            {},
            { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } },
          );
        } catch (inviteErr) {
          console.warn('Ref party added but platform invite failed:', inviteErr);
          setRefPartyError('Added — invite failed. Retry from the row.');
          fetchParticipants();
          return;
        }
      }

      setShowRefPartyModal(false);
      fetchParticipants();
    } catch (err) {
      setRefPartyError(formatBackendErrorMessage(err?.response?.data, err?.message || 'Failed to add reference party'));
    } finally {
      setIsAddingRefParty(false);
    }
  };

  const handleContinueInvite = () => {
    if (!selectedPartyType) {
      alert('Please select a party type');
      return;
    }
    setInviteFormData(getEmptyFormData());
    setInviteStep(2);
  };

  const handleBackToRoleSelection = () => {
    setInviteStep(1);
  };

  const handleInviteFormChange = (field, value) => {
    setInviteFormData(prev => ({ ...prev, [field]: value }));

    // Reset city when state changes for buyer
    if (field === 'preferredState') {
      setInviteFormData(prev => ({ ...prev, preferredState: value, preferredCity: '' }));
    }

    // Reset city when state changes for seller
    if (field === 'state') {
      setInviteFormData(prev => ({ ...prev, state: value, city: '' }));
    }
  };

  const handleInviteAddressSelect = (parsed) => {
    setInviteFormData(prev => ({
      ...prev,
      address: parsed.street || prev.address,
      ...(parsed.stateCode && {
        state: resolveToFullStateName(parsed.stateCode),
        city: parsed.city || '',
        zipCode: parsed.zip || '',
      }),
    }));
  };

  const handleInviteBuyerAddressSelect = (parsed) => {
    setInviteFormData(prev => ({
      ...prev,
      preferredAddress: parsed.street || prev.preferredAddress,
      ...(parsed.stateCode && {
        preferredState: resolveToFullStateName(parsed.stateCode),
        preferredCity: parsed.city || '',
        preferredZipCode: parsed.zip || '',
      }),
    }));
  };

  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  // Separate from isSendingInvitation/emailCheckResult on purpose — this only tracks the
  // "Resend" button shown on the wizard's duplicate-invitation error screen, so it can't be
  // confused with the pre-send email-exists check or the initial send-in-progress state.
  const [isResendingWizardInvite, setIsResendingWizardInvite] = useState(false);

  const handleSendInvitation = async () => {
    if (!inviteFormData.email.trim()) {
      alert('Please enter the email address');
      return;
    }
    if (!inviteFormData.firstName.trim()) {
      alert('Please enter the first name');
      return;
    }
    if (!inviteFormData.lastName.trim()) {
      alert('Please enter the last name');
      return;
    }

    // Validate transaction ID
    if (!transaction?.id) {
      alert('Transaction ID is missing');
      return;
    }

    setIsSendingInvitation(true);

    try {
      // Get Firebase token for authorization
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert('User not authenticated');
        setIsSendingInvitation(false);
        return;
      }

      const firebaseToken = await currentUser.getIdToken(true);

      // Determine client type based on selected party type
      const getClientType = (partyTypeId) => {
        switch (partyTypeId) {
          case 'seller':
            return 'seller';
          case 'buyer':
            return 'buyer';
          case 'co_listing_agent':
            return 'CO_LISTING_AGENT';
          case 'co_buyers_agent':
            return 'CO_BUYERS_AGENT';
          case 'broker':
            return 'BROKER';
          case 'transaction_coordinator':
            return 'TRANSACTION_COORDINATOR';
          case 'listing_agent':
            return 'LISTING_AGENT';
          case 'buyers_agent':
            return 'BUYERS_AGENT';
          case 'escrow_officer':
          case 'title_officer':
          case 'lender':
          case 'home_inspector':
          case 'appraiser':
          case 'attorney':
            return 'OTHER';
          default:
            return 'OTHER';
        }
      };

      const clientType = getClientType(selectedPartyType?.id);

      // Combine country code and phone digits into E.164 for the backend
      // (mirrors mobile's invite.service.ts normalization) — strip all
      // formatting so a US-style display value like "(555) 123-4567" still
      // reduces to digits before the country code is prefixed.
      const phoneDigits = inviteFormData.phone.trim().replace(/\D/g, '');
      const fullPhoneNumber = phoneDigits ? `${inviteFormData.countryCode}${phoneDigits}` : undefined;

      // Build request body based on the API requirements
      const requestBody = {
        transactionId: transaction.id,
        clientType: clientType,
        role: selectedPartyType?.id || undefined,
        roleName: selectedPartyType?.name || undefined,
        email: inviteFormData.email.trim(),
        firstName: inviteFormData.firstName.trim() || undefined,
        middleName: inviteFormData.middleName.trim() || undefined,
        lastName: inviteFormData.lastName.trim() || undefined,
        phone: fullPhoneNumber,
        company: inviteFormData.company.trim() || undefined,
        notes: inviteFormData.notes.trim() || undefined,
      };

      // Add seller-specific fields (listing property)
      if (selectedPartyType?.id === 'seller') {
        requestBody.propertyAddress = inviteFormData.address.trim() || undefined;
        requestBody.propertyCity = inviteFormData.city.trim() || undefined;
        requestBody.propertyState = inviteFormData.state.trim() || undefined;
        requestBody.propertyZip = inviteFormData.zipCode.trim() || undefined;
      }

      // Add buyer-specific fields (preferred location)
      if (selectedPartyType?.id === 'buyer') {
        requestBody.preferredState = inviteFormData.preferredState.trim() || undefined;
        requestBody.preferredCity = inviteFormData.preferredCity.trim() || undefined;
        requestBody.preferredZipCode = inviteFormData.preferredZipCode.trim() || undefined;
      }

      const authHeaders = {
        'Authorization': `Bearer ${firebaseToken}`,
        'Content-Type': 'application/json'
      };

      if (emailCheckResult?.exists && emailCheckResult?.user) {
        // ── User exists → add directly to transaction with role (POST /clients) ──
        // This immediately assigns the role so it appears in Switch Role.
        // The old invite + auto-accept approach used the inviter's token for accept,
        // which fails (forbidden) since accept requires the invitee's credentials.
        const checkedUser = emailCheckResult.user;
        const existingUserId = checkedUser.id || checkedUser.odooId || checkedUser.userId;
        console.log('Existing user found, adding directly to transaction:', existingUserId, 'role:', clientType.toUpperCase());
        await apiClient.post(
          API_ENDPOINTS.transactionClients(transaction.id),
          { userId: existingUserId, role: clientType.toUpperCase(), notes: requestBody.notes },
          { headers: authHeaders }
        );
      } else {
        // ── User doesn't exist → send invite ──
        console.log('New user, sending invitation:', requestBody);
        await apiClient.post(
          API_ENDPOINTS.inviteToTransaction(transaction.id),
          requestBody,
          { headers: authHeaders }
        );
      }

      const fullName = `${inviteFormData.firstName} ${inviteFormData.middleName} ${inviteFormData.lastName}`.replace(/\s+/g, ' ').trim();

      // Store invited person info for success screen
      setInvitedPersonInfo({
        name: fullName,
        email: inviteFormData.email,
        role: selectedPartyType.name
      });
      setInvitationError(null);

      // Move to success screen (step 3)
      setInviteStep(3);
    } catch (error) {
      console.error('Error sending invitation:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send invitation';

      // Store error info for error screen
      setInvitationError(errorMessage);
      setInvitedPersonInfo({
        name: `${inviteFormData.firstName} ${inviteFormData.lastName}`.trim(),
        email: inviteFormData.email,
        role: selectedPartyType?.name
      });

      // Move to result screen (step 3) to show error
      setInviteStep(3);
    } finally {
      setIsSendingInvitation(false);
    }
  };

  const handleCloseInviteModal = () => {
    setShowInvitePartyModal(false);
    setSelectedPartyType(null);
    setInviteStep(1);
    setInviteFormData(getEmptyFormData());

    // Reset email check states
    setEmailCheckResult(null);
    setEmailCheckError('');
    setIsCheckingEmail(false);
    // Reset invitation result states
    setInvitedPersonInfo(null);
    setInvitationError(null);
    setIsResendingWizardInvite(false);
  };

  const handleInviteAnotherParty = () => {
    // Reset to step 1 to invite another party
    setSelectedPartyType(null);
    setInviteStep(1);
    setInviteFormData(getEmptyFormData());

    // Reset email check states
    setEmailCheckResult(null);
    setEmailCheckError('');
    setIsCheckingEmail(false);
    // Reset invitation result states
    setInvitedPersonInfo(null);
    setInvitationError(null);
    setIsResendingWizardInvite(false);
  };

  const handleBackToTransaction = () => {
    // Close modal and go back to transaction
    handleCloseInviteModal();
    // Refresh participants list after successful invitation
    fetchParticipants();
  };

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Map participant role to clientType for the invite API
  const getClientTypeFromRole = (role) => {
    const r = (role || '').toLowerCase().replaceAll(' ', '_');
    if (r.includes('transaction_coordinator')) return 'TRANSACTION_COORDINATOR';
    // Co-agents must be checked before the primary agent rules below, which would
    // otherwise match them ("co_listing_agent" contains "listing_agent"). Covers the
    // backend value (CO_BUYERS_AGENT) and display names ("Co-Buyer's Agent").
    const isCoAgent = /^co[-_]/.test(r) && r.includes('agent');
    if (isCoAgent && r.includes('listing')) return 'CO_LISTING_AGENT';
    if (isCoAgent && r.includes('buyer')) return 'CO_BUYERS_AGENT';
    if (r.includes('listing_agent')) return 'LISTING_AGENT';
    if (r.includes('buyers_agent') || (r.includes('buyer') && r.includes('agent'))) return 'BUYERS_AGENT';
    if (r.includes('broker')) return 'BROKER';
    if (r.includes('buyer')) return 'buyer';
    if (r.includes('seller')) return 'seller';
    return 'OTHER';
  };

  // Resend invitation for pending participant
  const handleResendInvitation = async (e, participant) => {
    e.stopPropagation();
    if (!transaction?.id || !participant.email) return;

    try {
      const firebaseToken = await getFirebaseToken();
      const clientType = getClientTypeFromRole(participant.role);

      const requestBody = {
        clientType: clientType,
        email: participant.email
      };

      // Add optional fields if available
      const firstName = participant.firstName || participant.name?.split(' ')[0] || '';
      const lastName = participant.lastName || participant.name?.split(' ').slice(1).join(' ') || '';
      if (firstName) requestBody.firstName = firstName;
      if (lastName) requestBody.lastName = lastName;
      if (participant.phone) requestBody.phone = participant.phone;

      await apiClient.post(
        API_ENDPOINTS.inviteToTransaction(transaction.id),
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert(`Invitation resent to ${participant.email}`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert(error.response?.data?.message || 'Failed to resend invitation.');
    }
  };

  // Platform-invite handlers (ecosystem-growth flow). Separate from the
  // transaction-invite flow above — this invite grants no transaction access
  // on acceptance; the invitee lands in their role's home screen with the
  // inviting transaction invisible to them.
  const handleInviteToPlatform = async (e, participant) => {
    e.stopPropagation();
    if (!transaction?.id) return;
    setPlatformInvitingId(participant.id);
    setPlatformInviteError('');
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.participantInviteToPlatform(transaction.id, participant.id),
        {},
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      await fetchParticipants();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'EMAIL_REQUIRED') {
        // Open the prompt — collects email + phone then retries. Server
        // persists both onto the participant and sends the invite.
        setPlatformPromptParticipant(participant);
        setPlatformPromptEmail(participant.email || '');
        setPlatformPromptPhone(participant.phone || '');
        setPlatformPromptError('');
        setShowPlatformEmailPrompt(true);
      } else {
        const msg = err?.response?.data?.message || err.message || 'Failed to send invite';
        setPlatformInviteError(msg);
        alert(msg);
      }
    } finally {
      setPlatformInvitingId(null);
    }
  };

  const closePlatformEmailPrompt = () => {
    if (platformPromptSubmitting) return;
    setShowPlatformEmailPrompt(false);
    setPlatformPromptParticipant(null);
    setPlatformPromptEmail('');
    setPlatformPromptPhone('');
    setPlatformPromptError('');
  };

  const handlePlatformPromptSubmit = async () => {
    if (!platformPromptParticipant || !transaction?.id) return;
    const email = platformPromptEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setPlatformPromptError('Please enter a valid email.');
      return;
    }
    const phone = platformPromptPhone.trim() || undefined;
    try {
      setPlatformPromptSubmitting(true);
      setPlatformPromptError('');
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.participantInviteToPlatform(transaction.id, platformPromptParticipant.id),
        { email, ...(phone && { phone }) },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      await fetchParticipants();
      setShowPlatformEmailPrompt(false);
      setPlatformPromptParticipant(null);
      setPlatformPromptEmail('');
      setPlatformPromptPhone('');
    } catch (err) {
      setPlatformPromptError(err?.response?.data?.message || err.message || 'Could not send invite. Please try again.');
    } finally {
      setPlatformPromptSubmitting(false);
    }
  };

  // Resend action for the "pending invitation already exists" error on the invite wizard's
  // step-3 error screen. Deliberately reuses handleResendInvitation (the same function/
  // endpoint already used by the participant-list Resend icon) instead of writing a second,
  // independent resend implementation — adapts the wizard's inviteFormData/selectedPartyType
  // into the participant-shaped object that function expects.
  const handleResendWizardInvitation = async (e) => {
    const phoneDigits = inviteFormData.phone.trim().replace(/\D/g, '');
    const fullPhoneNumber = phoneDigits ? `${inviteFormData.countryCode}${phoneDigits}` : undefined;
    const wizardParticipant = {
      email: inviteFormData.email.trim(),
      role: selectedPartyType?.name || '',
      firstName: inviteFormData.firstName.trim(),
      lastName: inviteFormData.lastName.trim(),
      phone: fullPhoneNumber,
    };
    setIsResendingWizardInvite(true);
    try {
      await handleResendInvitation(e, wizardParticipant);
    } finally {
      setIsResendingWizardInvite(false);
    }
  };

  // Open delete confirmation popup
  const handleDeleteParticipant = (e, participant) => {
    e.stopPropagation();
    setDeleteTarget(participant);
    setShowDeleteModal(true);
  };

  // Confirm delete participant
  const confirmDeleteParticipant = async () => {
    if (!deleteTarget) return;
    const participantId = deleteTarget.id || deleteTarget.odooId;
    if (!transaction?.id || !participantId) return;

    setIsDeleting(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteParticipant(transaction.id, participantId),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      // If the deleted participant was selected, clear selection
      if (selectedParticipant?.id === deleteTarget.id) {
        setSelectedParticipant(null);
        setChatMessages([]);
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
      // Refresh participants
      fetchParticipants();
    } catch (error) {
      console.error('Error deleting participant:', error);
      alert(error.response?.data?.message || 'Failed to remove participant.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if selected participant is pending (group chat is never pending)
  const isSelectedPending = !isGroupChatSelected && selectedParticipant?.invitationStatus?.toUpperCase() === 'PENDING';
  const isClient = accessControl?.isClient();

  return (
    <>
      <div className="chat-parties-container">
        {/* Master List - Unified Participants */}
        <div className="cp-master-list">
          <div className="cp-master-header">
            <div className="cp-header-title-section">
              <div className="cp-master-title">Parties</div>
              <div className="cp-transaction-address">Transaction: {getTransactionAddress()}</div>
            </div>

            {/* Action buttons - only show if user can manage transaction */}
            {accessControl?.canManageTransaction() && (
              <div className="cp-party-action-btns">
                <button className="cp-invite-party-btn" onClick={handleInviteParty}>
                  <span>+</span>
                  <span>Invite Party</span>
                </button>
                <button className="cp-ref-party-btn" onClick={handleOpenRefPartyModal}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="19" y1="8" x2="19" y2="14"/>
                    <line x1="22" y1="11" x2="16" y2="11"/>
                  </svg>
                  <span>Ref Party</span>
                </button>
              </div>
            )}

            {/* Search */}
            <div className="cp-search-container">
              <span className="cp-search-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
              <input
                type="text"
                className="cp-search-input"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Transaction Inbox */}
          {transaction?.transactionEmail && (
            <div className="cp-inbox-card">
              <div className="cp-inbox-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span className="cp-inbox-title">Transaction Inbox</span>
              </div>
              <div className="cp-inbox-email-box">
                <span className="cp-inbox-email-text">{transaction.transactionEmail}</span>
              </div>
              <div className="cp-inbox-actions">
                <button className="cp-inbox-copy-btn" onClick={handleCopyInboxEmail}>
                  {copiedInboxEmail ? (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      Copy
                    </>
                  )}
                </button>
                <button className="cp-inbox-share-btn" onClick={handleShareInboxEmail}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  Share
                </button>
              </div>
            </div>
          )}

          <div className="cp-participant-count">
            {getTotalParticipants()} PARTIES IN THIS TRANSACTION
          </div>

          <div className="cp-master-list-content">
            {isLoadingParticipants ? (
              <div className="cp-loading-container">
                <div className="cp-loading-spinner"></div>
                <span>Loading parties...</span>
              </div>
            ) : participantsError ? (
              <div className="cp-empty-container">
                <div className="cp-empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
                <h3>Error Loading Parties</h3>
                <p>{participantsError}</p>
                <button className="cp-retry-btn" onClick={fetchParticipants}>
                  Try Again
                </button>
              </div>
            ) : getTotalParticipants() === 0 ? (
              <div className="cp-empty-container">
                <div className="cp-empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div>
                <h3>No Parties Yet</h3>
                <p>Invite parties to this transaction to get started.</p>
                {accessControl?.canManageTransaction() && (
                  <button className="cp-add-party-btn" onClick={handleInviteParty}>
                    + Invite Party
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Group Chat Entry */}
                {(resolvedTransactionChatId || transaction?.chatId) && (
                  <div className="cp-group-chat-section">
                    <div
                      className={`cp-participant-item cp-group-chat-entry ${isGroupChatSelected ? 'active' : ''}`}
                      onClick={handleGroupChatSelect}
                    >
                      <div className="cp-participant-avatar cp-group-chat-avatar" style={{ backgroundColor: '#5B47D6' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                      <div className="cp-participant-info">
                        <div className="cp-participant-name">Transaction Chat</div>
                        <div className="cp-participant-role-phone">
                          All parties &bull; {getTotalParticipants()} members
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Group */}
                {filterParticipants(participants.clients).length > 0 && (
                  <div className="cp-group-section">
                    <div className="cp-group-header">CLIENT</div>
                    <div className="cp-group-list">
                      {filterParticipants(participants.clients).map((participant) => {
                        const isSelf = isCurrentUser(participant);
                        const isRef = isReferenceParty(participant);
                        return (
                        <React.Fragment key={participant.id}>
                        <div
                          className={`cp-participant-item ${selectedParticipant?.id === participant.id && !isSelf && !isRef ? 'active' : ''} ${isSelf ? 'cp-participant-self' : ''}`}
                          onClick={() => !isSelf && !isRef && handleParticipantSelect(participant)}
                        >
                          {participant.profilePhotoUrl ? (
                            <img
                              src={participant.profilePhotoUrl}
                              alt={participant.name}
                              className="cp-participant-avatar-img"
                            />
                          ) : (
                            <div
                              className="cp-participant-avatar"
                              style={{ backgroundColor: isRef ? '#3B82F6' : participant.avatarColor }}
                            >
                              {participant.initials}
                            </div>
                          )}
                          <div className="cp-participant-info">
                            <div className="cp-participant-name">
                              {participant.name}
                              {isSelf && (
                                <span className="cp-you-badge">You</span>
                              )}
                              {!isSelf && participant.invitationStatus?.toUpperCase() === 'PENDING' && (
                                <span className="cp-pending-badge">Pending</span>
                              )}
                              {isRef && (
                                <span className="cp-reference-badge">Reference</span>
                              )}
                              {participant.platformInviteStatus === 'INVITED' && (
                                <span className="cp-platform-invited-badge">Invited to Rialty</span>
                              )}
                              {(participant.platformInviteStatus === 'JOINED' ||
                                participant.platformInviteStatus === 'ALREADY_MEMBER') && (
                                <span className="cp-platform-joined-badge">On Rialty</span>
                              )}
                            </div>
                            <div className="cp-participant-role-phone">
                              {participant.roleLabel} {participant.phone && `• ${participant.phone}`}
                            </div>
                          </div>
                          {isUserAgent() && (
                            <div className="cp-participant-actions">
                              {participant.invitationStatus?.toUpperCase() === 'PENDING' && (
                                <button
                                  className="cp-action-icon cp-resend-icon"
                                  title="Resend Invitation"
                                  onClick={(e) => handleResendInvitation(e, participant)}
                                >
                                  <img src={resendIcon} alt="Resend Invitation" width="20" height="20" style={{ display: 'block' }} />
                                </button>
                              )}
                              {/* Platform-invite: ecosystem-growth flow. Only
                                  shown for parties that aren't yet Rialty users
                                  and haven't been actioned yet (status null or
                                  NOT_INVITED). Clicking sends the invite directly
                                  when email is on file; else backend returns
                                  EMAIL_REQUIRED and the prompt modal collects
                                  email + phone before retrying. */}
                              {!participant.userId
                                && (!participant.platformInviteStatus
                                    || participant.platformInviteStatus === 'NOT_INVITED') && (
                                <button
                                  className="cp-action-icon cp-platform-invite-icon"
                                  title="Invite to Rialty"
                                  onClick={(e) => handleInviteToPlatform(e, participant)}
                                  disabled={platformInvitingId === participant.id}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <line x1="20" y1="8" x2="20" y2="14" />
                                    <line x1="23" y1="11" x2="17" y2="11" />
                                  </svg>
                                </button>
                              )}
                              <button
                                className="cp-action-icon cp-delete-icon"
                                title="Remove Participant"
                                onClick={(e) => handleDeleteParticipant(e, participant)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          )}
                          {/* Notes — private to the viewer, attached to this party.
                              Deliberately NOT gated by isUserAgent() — notes are
                              personal to whoever is viewing, unlike Resend/Remove
                              which are agent-only actions. Same record shown in
                              My Notes — not a copy. */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <NotesPanel
                              linkType="PARTY"
                              linkId={participant.id}
                              linkLabel={participant.name}
                              transactionId={transaction?.id}
                              transactionName={transaction?.transactionName || transaction?.propertyAddress}
                              portalTargetId={`party-notes-target-${participant.id}`}
                            />
                          </div>
                        </div>
                        {/* Portal target for this party's Notes toggle+stack — see the
                            NotesPanel usage above. Empty by default; takes no space
                            unless this party has notes or the composer is open. A
                            sibling of the row (not nested inside it) so it pushes
                            subsequent rows down in-flow instead of floating over them
                            — mirrors TransactionDocuments'/TransactionTimeline's own
                            portalTargetId usage; replaces the old `popover` flyout,
                            which visually overlapped the next party row when opened
                            inside this list's own scroll container. */}
                        <div id={`party-notes-target-${participant.id}`} className="txn-notes-block" />
                        </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Team Group */}
                {filterParticipants(participants.team).length > 0 && (
                  <div className="cp-group-section">
                    <div className="cp-group-header">TEAM</div>
                    <div className="cp-group-list">
                      {filterParticipants(participants.team).map((participant) => {
                        const isSelf = isCurrentUser(participant);
                        const isRef = isReferenceParty(participant);
                        return (
                        <React.Fragment key={participant.id}>
                        <div
                          className={`cp-participant-item ${selectedParticipant?.id === participant.id && !isSelf && !isRef ? 'active' : ''} ${isSelf ? 'cp-participant-self' : ''}`}
                          onClick={() => !isSelf && !isRef && handleParticipantSelect(participant)}
                        >
                          {participant.profilePhotoUrl ? (
                            <img
                              src={participant.profilePhotoUrl}
                              alt={participant.name}
                              className="cp-participant-avatar-img"
                            />
                          ) : (
                            <div
                              className="cp-participant-avatar"
                              style={{ backgroundColor: isRef ? '#3B82F6' : participant.avatarColor }}
                            >
                              {participant.initials}
                            </div>
                          )}
                          <div className="cp-participant-info">
                            <div className="cp-participant-name">
                              {participant.name}
                              {isSelf && (
                                <span className="cp-you-badge">You</span>
                              )}
                              {!isSelf && participant.invitationStatus?.toUpperCase() === 'PENDING' && (
                                <span className="cp-pending-badge">Pending</span>
                              )}
                              {isRef && (
                                <span className="cp-reference-badge">Reference</span>
                              )}
                              {participant.platformInviteStatus === 'INVITED' && (
                                <span className="cp-platform-invited-badge">Invited to Rialty</span>
                              )}
                              {(participant.platformInviteStatus === 'JOINED' ||
                                participant.platformInviteStatus === 'ALREADY_MEMBER') && (
                                <span className="cp-platform-joined-badge">On Rialty</span>
                              )}
                            </div>
                            <div className="cp-participant-role-phone">
                              {participant.roleLabel} {participant.phone && `• ${participant.phone}`}
                            </div>
                          </div>
                          {isUserAgent() && !isSelf && (
                            <div className="cp-participant-actions">
                              {participant.invitationStatus?.toUpperCase() === 'PENDING' && (
                                <button
                                  className="cp-action-icon cp-resend-icon"
                                  title="Resend Invitation"
                                  onClick={(e) => handleResendInvitation(e, participant)}
                                >
                                  <img src={resendIcon} alt="Resend Invitation" width="20" height="20" style={{ display: 'block' }} />
                                </button>
                              )}
                              {/* Platform-invite: ecosystem-growth flow. Only
                                  shown for parties that aren't yet Rialty users
                                  and haven't been actioned yet (status null or
                                  NOT_INVITED). Clicking sends the invite directly
                                  when email is on file; else backend returns
                                  EMAIL_REQUIRED and the prompt modal collects
                                  email + phone before retrying. */}
                              {!participant.userId
                                && (!participant.platformInviteStatus
                                    || participant.platformInviteStatus === 'NOT_INVITED') && (
                                <button
                                  className="cp-action-icon cp-platform-invite-icon"
                                  title="Invite to Rialty"
                                  onClick={(e) => handleInviteToPlatform(e, participant)}
                                  disabled={platformInvitingId === participant.id}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <line x1="20" y1="8" x2="20" y2="14" />
                                    <line x1="23" y1="11" x2="17" y2="11" />
                                  </svg>
                                </button>
                              )}
                              <button
                                className="cp-action-icon cp-delete-icon"
                                title="Remove Participant"
                                onClick={(e) => handleDeleteParticipant(e, participant)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          )}
                          {/* Notes — private to the viewer, attached to this party.
                              Deliberately NOT gated by isUserAgent() — notes are
                              personal to whoever is viewing, unlike Resend/Remove
                              which are agent-only actions. Same record shown in
                              My Notes — not a copy. */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <NotesPanel
                              linkType="PARTY"
                              linkId={participant.id}
                              linkLabel={participant.name}
                              transactionId={transaction?.id}
                              transactionName={transaction?.transactionName || transaction?.propertyAddress}
                              portalTargetId={`party-notes-target-${participant.id}`}
                            />
                          </div>
                        </div>
                        {/* Portal target for this party's Notes toggle+stack — see the
                            NotesPanel usage above (and the Client group's identical
                            comment above for why this replaced `popover`). */}
                        <div id={`party-notes-target-${participant.id}`} className="txn-notes-block" />
                        </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Detail View - Chat Window */}
        <div className="cp-detail-view">
          {(isGroupChatSelected || selectedParticipant) ? (
            <>
              {/* Chat Header */}
              <div className="cp-chat-header-bar">
                <div className="cp-chat-participant-info">
                  {isGroupChatSelected ? (
                    <div className="cp-chat-avatar cp-group-chat-avatar" style={{ backgroundColor: '#5B47D6' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                  ) : selectedParticipant.profilePhotoUrl ? (
                    <img
                      src={selectedParticipant.profilePhotoUrl}
                      alt={selectedParticipant.name}
                      className="cp-chat-avatar-img"
                    />
                  ) : (
                    <div
                      className="cp-chat-avatar"
                      style={{ backgroundColor: selectedParticipant.avatarColor }}
                    >
                      {selectedParticipant.initials}
                    </div>
                  )}
                  <div className="cp-participant-details">
                    <div className="cp-participant-name-large">
                      {isGroupChatSelected ? 'Transaction Chat' : selectedParticipant.name}
                    </div>
                    <div className="cp-participant-status">
                      {isGroupChatSelected
                        ? `All parties \u00B7 ${getTotalParticipants()} members`
                        : (selectedParticipant.roleLabel || selectedParticipant.role)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="cp-chat-messages">
                {isLoadingMessages ? (
                  <div className="cp-no-selection" style={{ padding: '40px 20px' }}>
                    <div className="cp-loading-spinner" style={{ margin: '0 auto 12px' }}></div>
                    <p>Loading messages...</p>
                  </div>
                ) : chatMessages.length === 0 ? (
                  <div className="cp-no-selection" style={{ padding: '40px 20px' }}>
                    <div className="cp-no-selection-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <>
                    {chatMessages.map((message) => {
                      // In group chat, use senderName for received message avatars
                      const senderName = isGroupChatSelected && !message.sent ? message.senderName : '';
                      const senderInitials = senderName
                        ? getInitials(null, null, senderName)
                        : (selectedParticipant?.initials || '??');
                      const senderColor = senderName
                        ? getAvatarColor(senderName)
                        : (selectedParticipant?.avatarColor || '#8E8E93');

                      return (
                        <div key={message.id} className={`cp-message ${message.sent ? 'sent' : 'received'}`}>
                          {!message.sent ? (
                            <div
                              className="cp-message-avatar"
                              style={{ backgroundColor: senderColor }}
                            >
                              {senderInitials}
                            </div>
                          ) : (
                            <div
                              className="cp-message-avatar"
                              style={{ backgroundColor: '#5B47D6' }}
                            >
                              ME
                            </div>
                          )}
                          <div className="cp-message-content">
                            {isGroupChatSelected && !message.sent && senderName && (
                              <div className="cp-message-sender-name">{senderName}</div>
                            )}
                            {(message.messageType === 'property' || message.messageType === 'PROPERTY') && message.propertyData ? (
                              <PropertyMessageCard
                                propertyData={message.propertyData}
                                onViewProperty={(mlsId) => navigate(`/property/${mlsId}`)}
                              />
                            ) : (
                              <div className="cp-message-bubble">
                                {/* Attachments */}
                                {message.attachments?.map((att, i) => {
                                  const mime = att.mimeType || '';
                                  const url = att.fileUrl || att.previewUrl || '';
                                  if (mime.startsWith('image/')) {
                                    return <img key={i} src={url} alt={att.fileName} className="cp-attachment-img" />;
                                  }
                                  if (mime.startsWith('video/')) {
                                    return <video key={i} src={url} controls className="cp-attachment-video" />;
                                  }
                                  if (mime.startsWith('audio/')) {
                                    return <audio key={i} src={url} controls className="cp-attachment-audio" />;
                                  }
                                  return (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={`cp-attachment-file ${message.sent ? 'sent' : 'received'}`}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                      <span className="cp-attachment-name">{att.fileName}</span>
                                      {att.fileSize > 0 && <span className="cp-attachment-size">{formatFileSize(att.fileSize)}</span>}
                                    </a>
                                  );
                                })}
                                {/* Link preview */}
                                {message.linkPreview?.title && (
                                  <a
                                    href={message.linkPreview.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`cp-link-preview-card ${message.sent ? 'sent' : 'received'}`}
                                  >
                                    {message.linkPreview.imageUrl && (
                                      <img src={message.linkPreview.imageUrl} alt="" className="cp-link-preview-img" />
                                    )}
                                    <div className="cp-link-preview-content">
                                      {message.linkPreview.siteName && (
                                        <div className="cp-link-preview-site">{message.linkPreview.siteName}</div>
                                      )}
                                      <div className="cp-link-preview-title">{message.linkPreview.title}</div>
                                      {message.linkPreview.description && (
                                        <div className="cp-link-preview-desc">{message.linkPreview.description}</div>
                                      )}
                                    </div>
                                  </a>
                                )}
                                {message.text && <div className="cp-message-text">{renderMessageText(message.text)}</div>}
                              </div>
                            )}
                            <div className="cp-message-time">{message.time}</div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input Area */}
              <div className={`cp-chat-input-area ${isSelectedPending ? 'cp-chat-disabled' : ''}`}>
                {isSelectedPending ? (
                  <div className="cp-chat-pending-notice">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>Chat is unavailable until this user accepts the invitation</span>
                  </div>
                ) : (
                  <>
                    {sendMessageError && (
                      <div className="cp-send-error">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {sendMessageError}
                      </div>
                    )}
                    {(isFetchingPreview || linkPreview) && (
                      <div className="cp-compose-link-preview">
                        {isFetchingPreview && !linkPreview ? (
                          <div className="cp-link-preview-loading">
                            <div className="cp-link-preview-spinner"></div>
                            <span>Loading preview...</span>
                          </div>
                        ) : linkPreview && (
                          <a href={linkPreview.url} target="_blank" rel="noopener noreferrer" className="cp-link-preview-card compose">
                            {linkPreview.imageUrl && (
                              <img src={linkPreview.imageUrl} alt="" className="cp-link-preview-img" />
                            )}
                            <div className="cp-link-preview-content">
                              {linkPreview.siteName && <div className="cp-link-preview-site">{linkPreview.siteName}</div>}
                              <div className="cp-link-preview-title">{linkPreview.title}</div>
                              {linkPreview.description && <div className="cp-link-preview-desc">{linkPreview.description}</div>}
                            </div>
                          </a>
                        )}
                        <button className="cp-link-preview-dismiss" onClick={handleDismissLinkPreview} title="Dismiss">✕</button>
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      onChange={handleFileSelect}
                    />
                    {pendingAttachment && (
                      <div className="cp-pending-attachment-bar">
                        {pendingAttachment.previewUrl ? (
                          <img src={pendingAttachment.previewUrl} alt="preview" className="cp-pending-attach-thumb" />
                        ) : (
                          <div className="cp-pending-attach-file">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            <span>{pendingAttachment.name}</span>
                          </div>
                        )}
                        <span className="cp-pending-attach-size">{formatFileSize(pendingAttachment.size)}</span>
                        <button className="cp-pending-attach-remove" onClick={removePendingAttachment} title="Remove">✕</button>
                      </div>
                    )}
                    <div className="cp-chat-input-container">
                      <div className="cp-chat-input-wrapper">
                        <textarea
                          className="cp-chat-input"
                          placeholder="Type a message..."
                          rows="1"
                          value={messageText}
                          onChange={(e) => handleTextChange(e.target.value)}
                          disabled={isUploadingAttachment}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                        <div
                          className="cp-attach-btn"
                          onClick={() => fileInputRef.current?.click()}
                          title="Attach file"
                          style={{ cursor: 'pointer' }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                        </div>
                      </div>
                      <div
                        className={`cp-send-btn ${((!messageText.trim() && !pendingAttachment) || isUploadingAttachment || isFetchingPreview) ? 'disabled' : ''}`}
                        onClick={handleSendMessage}
                      >
                        {isUploadingAttachment ? '...' : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="cp-no-selection">
              <div className="cp-no-selection-icon">💬</div>
              <h3>Select a conversation</h3>
              <p>Choose a participant to start chatting</p>
            </div>
          )}
        </div>
      </div>

      {/* Invite Party Modal */}
      {showInvitePartyModal && (
        <div className="invite-party-modal-overlay" onClick={handleCloseInviteModal}>
          <div className="invite-party-modal" onClick={(e) => e.stopPropagation()}>
            {/* Step 1: Select Role */}
            {inviteStep === 1 && (
              <>
                <div className="invite-party-header">
                  <h2 className="invite-party-title">Invite Party to Transaction</h2>
                  <p className="invite-party-address">{getFullAddress()}</p>
                </div>

                <div className="invite-party-body">
                  <div className="party-type-section">
                    <h3 className="party-type-label">Select Party Type/Role</h3>
                    <p className="party-type-sublabel">Choose the type of participant you want to invite</p>
                  </div>

                  <div className="party-types-grid">
                    {partyTypes.map((type) => (
                      <div
                        key={type.id}
                        className={`party-type-card ${selectedPartyType?.id === type.id ? 'selected' : ''}`}
                        onClick={() => setSelectedPartyType(type)}
                      >
                        <div className="party-type-icon-wrapper" style={{ backgroundColor: `${type.color}15` }}>
                          <span className="party-type-icon">{type.icon}</span>
                        </div>
                        <div className="party-type-info">
                          <div className="party-type-name">{type.name}</div>
                        </div>
                        <div className="party-type-checkbox">
                          {selectedPartyType?.id === type.id ? (
                            <div className="checkbox-checked">✓</div>
                          ) : (
                            <div className="checkbox-unchecked"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="invite-party-footer">
                  <button className="invite-cancel-btn" onClick={handleCloseInviteModal}>
                    Cancel
                  </button>
                  <button className="invite-continue-btn" onClick={handleContinueInvite}>
                    Continue
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Enter Details Form */}
            {inviteStep === 2 && (
              <>
                <div className="invite-form-header">
                  <h2 className="invite-form-title">
                    {selectedPartyType?.id === 'buyer' ? "Buyer's Details" :
                     selectedPartyType?.id === 'seller' ? "Seller's Details" :
                     `Invite ${selectedPartyType?.name}`}
                  </h2>
                  <p className="invite-form-subtitle">Enter their contact information and set permissions</p>
                </div>

                <div className="invite-form-body">
                  {/* Email Address - Always on top */}
                  <div className="invite-form-group">
                    <label className="invite-form-label">Email Address</label>
                    <div className="invite-email-input-wrapper">
                      <input
                        type="email"
                        className={`invite-form-input ${emailCheckResult?.exists ? 'email-exists' : ''} ${emailCheckError ? 'email-error' : ''}`}
                        placeholder="email@example.com"
                        value={inviteFormData.email}
                        onChange={(e) => {
                          handleInviteFormChange('email', e.target.value);
                          // Reset check result when email changes
                          setEmailCheckResult(null);
                          setEmailCheckError('');
                        }}
                        onBlur={handleEmailBlur}
                      />
                      {isCheckingEmail && (
                        <span className="email-check-loading">Checking...</span>
                      )}
                    </div>
                    {emailCheckResult && (
                      <div className={`email-check-message ${emailCheckResult.exists ? 'exists' : 'not-exists'}`}>
                        {emailCheckResult.exists
                          ? '✓ User found - details auto-filled'
                          : 'New user - please fill in the details'}
                      </div>
                    )}
                    {emailCheckError && (
                      <div className="email-check-message error">
                        {emailCheckError}
                      </div>
                    )}
                  </div>

                  {/* Name Fields - First, Middle, Last */}
                  <div className="invite-form-group">
                    <label className="invite-form-label">First Name</label>
                    <input
                      type="text"
                      className="invite-form-input"
                      placeholder="First Name"
                      value={inviteFormData.firstName}
                      onChange={(e) => handleInviteFormChange('firstName', e.target.value)}
                    />
                  </div>

                  <div className="invite-form-group">
                    <label className="invite-form-label">Middle Name (Optional)</label>
                    <input
                      type="text"
                      className="invite-form-input"
                      placeholder="Middle Name"
                      value={inviteFormData.middleName}
                      onChange={(e) => handleInviteFormChange('middleName', e.target.value)}
                    />
                  </div>

                  <div className="invite-form-group">
                    <label className="invite-form-label">Last Name</label>
                    <input
                      type="text"
                      className="invite-form-input"
                      placeholder="Last Name"
                      value={inviteFormData.lastName}
                      onChange={(e) => handleInviteFormChange('lastName', e.target.value)}
                    />
                  </div>

                  {/* Phone Number with Country Code */}
                  <div className="invite-form-group">
                    <label className="invite-form-label">Phone Number (Optional)</label>
                    <div className="invite-phone-input-wrapper">
                      {/*<select
                        className="invite-country-code-select"
                        value={inviteFormData.countryCode}
                        onChange={(e) => handleInviteFormChange('countryCode', e.target.value)}
                      >
                        {countryCodes.map((country, index) => (
                          <option key={`${country.code}-${country.country}-${index}`} value={country.code}>
                            {country.flag} {country.code} ({country.country})
                          </option>
                        ))}
                      </select>
                      */}
                      <input
                        type="tel"
                        className="invite-phone-input"
                        placeholder="(555) 789-0123"
                        value={inviteFormData.phone}
                        onChange={(e) => handleInviteFormChange('phone', formatUSPhoneInput(e.target.value))}
                      />
                    </div>
                  </div>

                  {/* Buyer-specific fields */}
                  {selectedPartyType?.id === 'buyer' && (
                    <>
                      <div className="invite-form-group">
                        <label className="invite-form-label">Address (Optional)</label>
                        <AddressAutocomplete
                          value={inviteFormData.preferredAddress}
                          onChange={(val) => handleInviteFormChange('preferredAddress', val)}
                          onAddressSelect={handleInviteBuyerAddressSelect}
                          placeholder="Enter address"
                          inputClassName="invite-form-input"
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">State (Optional)</label>
                        <input
                          type="text"
                          className="invite-form-input"
                          placeholder="State"
                          value={inviteFormData.preferredState}
                          onChange={(e) => handleInviteFormChange('preferredState', e.target.value)}
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">City (Optional)</label>
                        <input
                          type="text"
                          className="invite-form-input"
                          placeholder="City"
                          value={inviteFormData.preferredCity}
                          onChange={(e) => handleInviteFormChange('preferredCity', e.target.value)}
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">Zip Code (Optional)</label>
                        <input
                          type="text"
                          className="invite-form-input"
                          placeholder="Zip Code"
                          value={inviteFormData.preferredZipCode}
                          onChange={(e) => handleInviteFormChange('preferredZipCode', e.target.value)}
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">Notes (Optional)</label>
                        <textarea
                          className="invite-form-textarea"
                          placeholder="Add notes (optional)"
                          value={inviteFormData.notes}
                          onChange={(e) => handleInviteFormChange('notes', e.target.value)}
                          rows="3"
                        />
                      </div>
                    </>
                  )}

                  {/* Seller-specific fields - Pre-filled from transaction */}
                  {selectedPartyType?.id === 'seller' && (
                    <>
                      <div className="invite-form-group">
                        <label className="invite-form-label">Property Address (Optional)</label>
                        <AddressAutocomplete
                          value={inviteFormData.address}
                          onChange={(val) => handleInviteFormChange('address', val)}
                          onAddressSelect={handleInviteAddressSelect}
                          placeholder="Property Address"
                          inputClassName="invite-form-input"
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">State (Optional)</label>
                        <input
                          type="text"
                          className="invite-form-input"
                          placeholder="State"
                          value={inviteFormData.state}
                          onChange={(e) => handleInviteFormChange('state', e.target.value)}
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">City (Optional)</label>
                        <input
                          type="text"
                          className="invite-form-input"
                          placeholder="City"
                          value={inviteFormData.city}
                          onChange={(e) => handleInviteFormChange('city', e.target.value)}
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">Zip Code (Optional)</label>
                        <input
                          type="text"
                          className="invite-form-input"
                          placeholder="Zip Code"
                          value={inviteFormData.zipCode}
                          onChange={(e) => handleInviteFormChange('zipCode', e.target.value)}
                        />
                      </div>

                      <div className="invite-form-group">
                        <label className="invite-form-label">Notes (Optional)</label>
                        <textarea
                          className="invite-form-textarea"
                          placeholder="Add notes (optional)"
                          value={inviteFormData.notes}
                          onChange={(e) => handleInviteFormChange('notes', e.target.value)}
                          rows="3"
                        />
                      </div>
                    </>
                  )}

                  {/* Other party types - Company field */}
                  {selectedPartyType?.id !== 'buyer' && selectedPartyType?.id !== 'seller' && (
                    <div className="invite-form-group">
                      <label className="invite-form-label">Company/Organization (Optional)</label>
                      <input
                        type="text"
                        className="invite-form-input"
                        placeholder="Company Name"
                        value={inviteFormData.company}
                        onChange={(e) => handleInviteFormChange('company', e.target.value)}
                      />
                    </div>
                  )}
                </div>

                <div className="invite-party-footer">
                  <button className="invite-back-btn" onClick={handleBackToRoleSelection} disabled={isSendingInvitation}>
                    Back
                  </button>
                  <button className="invite-send-btn" onClick={handleSendInvitation} disabled={isSendingInvitation}>
                    {isSendingInvitation ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Success or Error Screen */}
            {inviteStep === 3 && (
              <>
                {!invitationError ? (
                  /* Success Screen */
                  <>
                    <div className="invite-result-header">
                      <h2 className="invite-result-header-title">Invitation Sent</h2>
                      <p className="invite-result-header-subtitle">Confirmation and pending status</p>
                    </div>
                    <div className="invite-result-container">
                      <div className="invite-result-icon success">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <h2 className="invite-result-title">Invitation Sent!</h2>
                      <p className="invite-result-subtitle">
                        We've sent an invitation to {invitedPersonInfo?.name} at {invitedPersonInfo?.email}
                      </p>

                      <div className="invite-next-steps-card">
                        <h3 className="invite-next-steps-title">What happens next?</h3>

                        <div className="invite-step-item">
                          <div className="invite-step-number">1</div>
                          <div className="invite-step-content">
                            <div className="invite-step-heading">Email Delivered</div>
                            <div className="invite-step-desc">
                              {invitedPersonInfo?.name?.split(' ')[0]} will receive an invitation email with a link to join
                            </div>
                          </div>
                        </div>

                        <div className="invite-step-item">
                          <div className="invite-step-number">2</div>
                          <div className="invite-step-content">
                            <div className="invite-step-heading">Account Setup</div>
                            <div className="invite-step-desc">
                              If new to Rialty, they'll create an account (takes 2 minutes)
                            </div>
                          </div>
                        </div>

                        <div className="invite-step-item">
                          <div className="invite-step-number">3</div>
                          <div className="invite-step-content">
                            <div className="invite-step-heading">Access Granted</div>
                            <div className="invite-step-desc">
                              They'll appear in Parties with their assigned permissions
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="invite-pending-notice">
                        <span className="invite-pending-icon">&#9203;</span>
                        <div className="invite-pending-content">
                          <div className="invite-pending-title">Pending Acceptance</div>
                          <div className="invite-pending-desc">
                            {invitedPersonInfo?.name?.split(' ')[0]} will appear in your parties list with a "Pending" status until they accept the invitation.
                          </div>
                        </div>
                      </div>

                      <div className="invite-result-footer">
                        <button className="invite-another-btn" onClick={handleInviteAnotherParty}>
                          Invite Another Party
                        </button>
                        <button className="invite-back-transaction-btn" onClick={handleBackToTransaction}>
                          Back to Transaction
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Error Screen */
                  <>
                    <div className="invite-result-header error">
                      <h2 className="invite-result-header-title">Invitation Failed</h2>
                      <p className="invite-result-header-subtitle">Something went wrong</p>
                    </div>
                    <div className="invite-result-container">
                      <div className="invite-result-icon error">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <h2 className="invite-result-title error">Invitation Failed</h2>
                      <p className="invite-result-subtitle">
                        We couldn't send the invitation to {invitedPersonInfo?.email}
                      </p>

                      <div className="invite-error-card">
                        <div className="invite-error-message">
                          {invitationError}
                        </div>
                      </div>

                      {isPendingInvitationError(invitationError) && (
                        <div className="invite-result-footer">
                          <button
                            className="invite-another-btn"
                            onClick={handleResendWizardInvitation}
                            disabled={isResendingWizardInvite}
                          >
                            {isResendingWizardInvite ? 'Resending...' : 'Resend Invitation'}
                          </button>
                        </div>
                      )}

                      <div className="invite-result-footer">
                        <button className="invite-another-btn" onClick={handleInviteAnotherParty}>
                          Try Again
                        </button>
                        <button className="invite-back-transaction-btn" onClick={handleBackToTransaction}>
                          Back to Transaction
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Reference Party Modal */}
      {showRefPartyModal && (
        <div className="invite-party-modal-overlay rp-modal-overlay" onClick={handleCloseRefPartyModal}>
          <div className="invite-party-modal rp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rp-modal-header">
              <h2 className="rp-modal-title">Add Reference Party</h2>
              <p className="rp-modal-subtitle">
                Check email first — if they're on Rialty we'll add them; otherwise we'll send an invite.
              </p>
            </div>

            <div className="rp-modal-body">
              {/* Email — the entry point. Check fires on blur / Enter and
                  auto-fills name/phone when a Rialty user is matched. The
                  rest of the form is always visible so the agent can fill
                  it in parallel; on submit, if email was never checked we
                  run the check first to decide the Add vs Invite branch. */}
              <div className="invite-form-group">
                <label className="rp-form-label">Email <span className="rp-required">*</span></label>
                <div className="rp-email-row">
                  <input
                    type="email"
                    className="invite-form-input rp-email-input"
                    placeholder="party@example.com"
                    value={refPartyForm.email}
                    onChange={(e) => {
                      setRefPartyForm(prev => ({ ...prev, email: e.target.value }));
                      if (refEmailChecked || refExistingUser || refEmailStatusMessage) {
                        setRefEmailChecked(false);
                        setRefExistingUser(null);
                        setRefEmailStatusMessage('');
                      }
                    }}
                    onBlur={() => {
                      if (refPartyForm.email.trim() && !refExistingUser && !refEmailChecked && !refEmailChecking) {
                        runRefPartyEmailCheck();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        runRefPartyEmailCheck();
                      }
                    }}
                    disabled={refEmailChecking}
                    autoFocus
                  />
                  {refEmailChecking && (
                    <span className="rp-email-spinner" aria-label="Checking email" />
                  )}
                </div>
                {refExistingUser && (
                  <div className="rp-email-status rp-email-status-success">
                    ✓ Found on Rialty: {[refExistingUser.firstName, refExistingUser.lastName].filter(Boolean).join(' ') || refExistingUser.email}
                  </div>
                )}
                {refEmailChecked && !refExistingUser && !refEmailStatusMessage && (
                  <div className="rp-email-status rp-email-status-info">
                    No account yet — we'll send an invite on save.
                  </div>
                )}
                {refEmailStatusMessage && (
                  <div className="rp-email-status rp-email-status-error">{refEmailStatusMessage}</div>
                )}
              </div>

              {/* Rest of the form is always open — same shape as the
                  "Invite Party to Transaction" modal. Role sits right under
                  Email so the agent picks it before filling in details. The
                  email check still runs (auto-fills name/phone when the user
                  is found) and handleAddRefPartySubmit runs it synchronously
                  at submit time if the agent hasn't triggered it yet, so the
                  server branch is still chosen correctly. */}
              <div className="invite-form-group">
                <label className="rp-form-label">Role <span className="rp-required">*</span></label>
                <select
                  className="invite-form-input rp-role-select"
                  value={refPartyForm.role}
                  onChange={(e) => setRefPartyForm(prev => ({ ...prev, role: e.target.value }))}
                >
                  <option value="" disabled>Select Role</option>
                  {refPartyRoles.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                  {refPartyServiceTypes.map((t) => {
                    const value = `${REF_PARTY_SERVICE_TYPE_PREFIX}${t.id}`;
                    return <option key={value} value={value}>{t.name}</option>;
                  })}
                </select>
              </div>

              <div className="invite-form-group">
                <label className="rp-form-label">First Name <span className="rp-required">*</span></label>
                <input
                  type="text"
                  className="invite-form-input"
                  placeholder="First name"
                  value={refPartyForm.firstName}
                  onChange={(e) => setRefPartyForm(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </div>

              <div className="invite-form-group">
                <label className="rp-form-label">Middle Name</label>
                <input
                  type="text"
                  className="invite-form-input"
                  placeholder="Middle name (optional)"
                  value={refPartyForm.middleName}
                  onChange={(e) => setRefPartyForm(prev => ({ ...prev, middleName: e.target.value }))}
                />
              </div>

              <div className="invite-form-group">
                <label className="rp-form-label">Last Name <span className="rp-required">*</span></label>
                <input
                  type="text"
                  className="invite-form-input"
                  placeholder="Last name"
                  value={refPartyForm.lastName}
                  onChange={(e) => setRefPartyForm(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </div>

              <div className="invite-form-group">
                <label className="rp-form-label">Phone</label>
                <input
                  type="tel"
                  className="invite-form-input"
                  placeholder="Phone number (optional)"
                  value={refPartyForm.phone}
                  onChange={(e) => setRefPartyForm(prev => ({ ...prev, phone: formatUSPhoneInput(e.target.value) }))}
                />
              </div>

              {refPartyError && (
                <div className="rp-error-msg">{refPartyError}</div>
              )}
            </div>

            <div className="invite-party-footer">
              <button className="invite-cancel-btn" onClick={handleCloseRefPartyModal} disabled={isAddingRefParty}>
                Cancel
              </button>
              {/* Submit always enabled once the user is typing — on click,
                  handleAddRefPartySubmit runs the email check itself if the
                  agent never triggered it (lines ~1796-1802), so the correct
                  Add-vs-Invite branch is still chosen. Label reflects what
                  the check already found. */}
              {(() => {
                const buttonDisabled = isAddingRefParty || refEmailChecking;
                const buttonLabel = isAddingRefParty
                  ? 'Adding...'
                  : refEmailChecking
                    ? 'Checking...'
                    : refExistingUser
                      ? 'Add Reference'
                      : 'Add & Send Invite';
                return (
                  <button
                    className="invite-continue-btn"
                    onClick={handleAddRefPartySubmit}
                    disabled={buttonDisabled}
                  >
                    {buttonLabel}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="cp-delete-modal-overlay" onClick={() => { if (!isDeleting) { setShowDeleteModal(false); setDeleteTarget(null); } }}>
          <div className="cp-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-delete-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <div className="cp-delete-modal-title">Remove Participant</div>
            <div className="cp-delete-modal-desc">
              Are you sure you want to remove <strong>{deleteTarget.name}</strong> from this transaction? This action cannot be undone.
            </div>
            <div className="cp-delete-modal-actions">
              <button
                className="cp-delete-modal-btn cp-delete-modal-yes"
                onClick={confirmDeleteParticipant}
                disabled={isDeleting}
              >
                {isDeleting ? 'Removing...' : 'Yes, Remove'}
              </button>
              <button
                className="cp-delete-modal-btn cp-delete-modal-no"
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                disabled={isDeleting}
              >
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Platform-invite email prompt — opens when the backend returns
          EMAIL_REQUIRED for a party with no email on file. On submit, retries
          the invite with the email + optional phone in the body (which the
          server also persists onto the Participant record). */}
      {showPlatformEmailPrompt && (
        <div className="invite-party-modal-overlay cp-platform-prompt-overlay" onClick={closePlatformEmailPrompt}>
          <div className="invite-party-modal cp-platform-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rp-modal-header">
              <h2 className="rp-modal-title">Invite to Rialty</h2>
              <p className="rp-modal-subtitle">
                {platformPromptParticipant?.name
                  ? `${platformPromptParticipant.name} has no email on file. Add one to send the invite.`
                  : 'Add an email to send the invite.'}
              </p>
            </div>
            <div className="rp-modal-body">
              <div className="invite-form-group">
                <label className="rp-form-label">Email <span className="rp-required">*</span></label>
                <input
                  type="email"
                  className="invite-form-input"
                  placeholder="party@example.com"
                  value={platformPromptEmail}
                  onChange={(e) => setPlatformPromptEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="invite-form-group">
                <label className="rp-form-label">Phone</label>
                <input
                  type="tel"
                  className="invite-form-input"
                  placeholder="+1 (555) 555-0100 (optional)"
                  value={platformPromptPhone}
                  onChange={(e) => setPlatformPromptPhone(e.target.value)}
                />
              </div>
              {platformPromptError && (
                <div className="cp-platform-prompt-error">{platformPromptError}</div>
              )}
            </div>
            {/* Reuse the invite-party footer + button classes — the earlier
                rp-modal-footer / rp-cancel-btn / rp-submit-btn names had no
                CSS defined anywhere in the project, so the buttons rendered
                as unstyled browser defaults. The sibling AddReferenceParty
                modal in this file uses the same pattern below. The two
                override styles below right-align the pair (the footer's
                flex: 1 on each button would otherwise stretch them full-
                width) and keep the auto width that primary / secondary CTA
                conventions expect in a dialog footer. */}
            <div className="invite-party-footer" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="invite-cancel-btn"
                style={{ flex: '0 0 auto' }}
                onClick={closePlatformEmailPrompt}
                disabled={platformPromptSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="invite-send-btn"
                style={{ flex: '0 0 auto' }}
                onClick={handlePlatformPromptSubmit}
                /* Email must look valid before Send is clickable (expected #4).
                   Simple RFC-ish check — same shape used by the sibling
                   AddReferenceParty flow's refEmailChecking gate. */
                disabled={
                  platformPromptSubmitting
                  || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(platformPromptEmail.trim())
                }
              >
                {platformPromptSubmitting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionChatParties;
